import BulkCsvImport from './BulkCsvImport';
import OpeningStockDispatchForm from './OpeningStockDispatchForm';
import { DISPATCH_REFERENCE_HEADERS } from '../utils/stockDispatchExport';
import { csvNumber, downloadCsv } from '../utils/bulkCsv';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { calculateStagePrice, calculateStageBudgetGrams, proportionalPackMrp, resolvePricingConfig, explainPricingMismatch } from '../utils/marketPricing';

import {
  createInventoryDispatchOrder,
  fetchInventoryDispatchOrders,
  fetchInventoryProducts,
  fetchStockTransactions,
  updateInventoryDispatchStatus,
  completeInternalPackingDispatch,
} from '../features/inventory/stockManagerInventorySlice';
import {
  getDispatchDestinationLabel,
  getDispatchItemBrand,
  getDispatchItemProductName,
  getWarehouseLabel,
} from '../utils/dispatchDisplay';
import { calculatePackPricingFromRow } from '../utils/packPricing';
import { getDispatchPricing } from '../utils/dispatchPricing';
import { loadDispatchPricing } from '../utils/loadDispatchPricing';

// const getDateOnly = (value) => {
//   if (!value) return '';
//   const text = String(value).trim();
//   const match = text.match(/(\d{4})-(\d{2})-(\d{2})/);
//   return match ? match[0] : '';
// };

export const packGrams = (row) => {
  const unit = String(row.unit_short_code || row.unit_name || row.units || row.unit || '').trim().toLowerCase();
  const qty = Number(row.barcode_quantity || row.quantity || 1);
  if (unit.startsWith('kg')) return qty * 1000;
  if (unit.startsWith('gm') || unit === 'g') return qty;
  return null;
};
// One search line per product: biggest pack first, then earliest expiry.
export const groupInventorySuggestions = (rows, limit = 20) => {
  const groups = new Map();
  for (const row of rows) {
    const key = [row.product_id || row.product_code || row.display_product_name, row.brand_name_english || ''].join('|');
    if (!groups.has(key)) groups.set(key, { key, rows: [] });
    groups.get(key).rows.push(row);
  }
  return [...groups.values()].slice(0, limit).map(group => ({ ...group, rows: [...group.rows].sort((a, b) =>
    (packGrams(b) ?? 0) - (packGrams(a) ?? 0)
    || String(a.exp_date_only || '9999').localeCompare(String(b.exp_date_only || '9999'))) }));
};

const CreateDispatchOrderSection = ({
  inventoryProducts = [],
  catalogBarcodes = [],
  dispatchStakeholders = [],
  warehouses = [],
  outlets = [],
  loading = false,
  ratePlans = [],
  units = [],
}) => {
  const dispatch = useDispatch();
  const token = useSelector(state => state.posUser?.userInfo?.token || '');
  const [packingLedgers, setPackingLedgers] = useState([]);
  const [packingLedgerError, setPackingLedgerError] = useState('');
  const [pricingWarning, setPricingWarning] = useState('');
  const pricingRequest = useRef(0);
  const [presetPanels, setPresetPanels] = useState({});
  const [marketRates, setMarketRates] = useState([]);
  const [loadingPricing, setLoadingPricing] = useState(false);
  const refreshPricing = useCallback(async () => {
    if (!token) return;
    const request = ++pricingRequest.current;
    setLoadingPricing(true);
    try {
      const result = await loadDispatchPricing(token);
      if (request !== pricingRequest.current) return;
      setPackingLedgers(result.ledgers); setMarketRates(result.markets);
      setPackingLedgerError(result.error); setPricingWarning(result.warning);
    } catch (error) {
      if (request === pricingRequest.current) { setPackingLedgers([]); setMarketRates([]); setPackingLedgerError(error.message); setPricingWarning(''); }
    }
    finally { if (request === pricingRequest.current) setLoadingPricing(false); }
  }, [token]);
  useEffect(() => {
    window.addEventListener('focus', refreshPricing);
    return () => window.removeEventListener('focus', refreshPricing);
  }, [refreshPricing]);

  const [sourceWarehouseId, setSourceWarehouseId] = useState('');
  const [destinationType, setDestinationType] = useState('outlet');
  const [destinationId, setDestinationId] = useState('');
  useEffect(() => { refreshPricing(); }, [sourceWarehouseId, destinationType, destinationId, refreshPricing]);
  const [expectedDispatchAt, setExpectedDispatchAt] = useState('');
  const [dispatchNotes, setDispatchNotes] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [search, setSearch] = useState('');
  const [items, setItems] = useState([]);
  const [dynamicPackForms, setDynamicPackForms] = useState({});
  const inventorySearchRef = useRef(null);

  useEffect(() => {
    const closeSuggestionsOnOutsideClick = (event) => {
      if (
        inventorySearchRef.current &&
        !inventorySearchRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', closeSuggestionsOnOutsideClick);

    return () => {
      document.removeEventListener('mousedown', closeSuggestionsOnOutsideClick);
    };
  }, []);

  const destinations = useMemo(() => {
    if (destinationType === 'internal_packing') {
      return [{ id: 'internal-packing', name: 'Internal Packing Dept' }];
    }

    if (destinationType === 'warehouse') return warehouses;
    if (destinationType === 'outlet') return outlets;
    if (destinationType === 'vendor' || destinationType === 'customer') {
      return dispatchStakeholders.filter((stakeholder) =>
        String(stakeholder.stakeholder_type || '')
          .toLowerCase()
          .includes(destinationType)
      );
    }
    return [];
  }, [destinationType, warehouses, outlets, dispatchStakeholders]);

  const sourceWarehouse = warehouses.find(
    (w) => String(w.id) === String(sourceWarehouseId)
  );

  const selectedDestination = destinations.find(
    (d) => String(d.id) === String(destinationId)
  );

  const isInternalPacking = destinationType === 'internal_packing';
  const ratePlansByBarcodeId = useMemo(
    () =>
      (ratePlans || []).reduce((acc, plan) => {
        const key = String(plan.product_barcode_id);
        const list = acc.get(key) || [];
        list.push(plan);
        acc.set(key, list);
        return acc;
      }, new Map()),
    [ratePlans]
  );

  const normalizeText = (value) =>
    String(value || '').toLowerCase().replace(/\s+/g, ' ').trim();

  const normalizeUnit = (value) =>
    String(value || '').toLowerCase().replace(/\./g, '').replace(/\s+/g, '');

  const formatPackUnit = (item) =>
    [
      item.barcode_quantity || item.quantity || item.catalog_quantity,
      item.unit_short_code || item.unit_name || item.units || item.unit,
    ]
      .filter(Boolean)
      .join(' ') || '-';

  const formatDecimal = (value, digits = 3) => {
    const numeric = Number(value);

    if (!Number.isFinite(numeric)) return '0';

    return numeric.toFixed(digits).replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
  };

  const padBarcodePart = (value, size) => String(value || '').padStart(size, '0');

  const makeMkBarcode = ({ product_id, brand_id, category_id, unit_id, quantity }) => {
    const productId = Number(product_id || 0);
    const brandId = Number(brand_id || 0);
    const categoryId = Number(category_id || 0);
    const unitId = Number(unit_id || 0);
    const packQuantity = Number(quantity || 0);

    if (!productId || !brandId || !categoryId || !unitId || !packQuantity) return '';
    if (
      productId > 9999 ||
      brandId > 999 ||
      categoryId > 99 ||
      unitId > 99 ||
      packQuantity > 999
    ) {
      return '';
    }

    return (
      '890' +
      padBarcodePart(productId, 4) +
      padBarcodePart(brandId, 3) +
      padBarcodePart(categoryId, 2) +
      padBarcodePart(unitId, 2) +
      padBarcodePart(packQuantity, 3)
    );
  };

  const getItemUnitQtyInGrams = (item) => {
    const qty = Number(item.barcode_quantity || item.quantity || 1);
    const unit = normalizeUnit(item.unit_short_code || item.unit_name || item.units);

    if (unit.startsWith('kg')) return qty * 1000;
    if (unit.startsWith('gm') || unit.startsWith('gms') || unit === 'g') return qty;

    return qty;
  };

  const getQuantityBaseUnits = (quantity, unitCode) => {
    const qty = Number(quantity || 0);
    const unit = normalizeUnit(unitCode);

    if (!qty) return 0;
    if (unit.startsWith('kg')) return qty * 1000;
    if (unit.startsWith('gm') || unit.startsWith('gms') || unit === 'g') return qty;

    return qty;
  };

  const isWeightUnit = (item) => {
    const unit = normalizeUnit(item.unit_short_code || item.unit_name || item.units || item.unit);
    return (
      unit.startsWith('kg') ||
      unit.startsWith('gm') ||
      unit.startsWith('gms') ||
      unit === 'g'
    );
  };

  const getSourcePackBaseUnits = (item) =>
    getQuantityBaseUnits(
      item.source_pack_quantity,
      item.source_pack_unit_short_code || getUnitById(item.source_pack_unit_id).unit_short_code
    );

  const firstNumber = (...values) => {
    for (const value of values) {
      if (value === undefined || value === null || value === '') continue;

      const numeric = Number(String(value).replace(/,/g, '').trim());
      if (Number.isFinite(numeric) && numeric > 0) return numeric;
    }

    return 0;
  };

  const getSourceUnitPrice = (item) => {
    const inventoryMatch = inventoryProducts.find(
      (row) =>
        String(row.id || row.inventory_product_id) ===
        String(item.inventory_product_id)
    );

    const price = firstNumber(
      item.source_unit_price,
      calculatePackPricingFromRow(inventoryMatch || item).costPerPiece,
      item.landed_purchase_unit_price,
      item.landedPurchaseUnitPrice,
      item.final_unit_cost,
      item.finalUnitCost,
      item.landed_unit_price,
      item.landedUnitPrice,
      inventoryMatch?.landed_purchase_unit_price,
      inventoryMatch?.landedPurchaseUnitPrice,
      inventoryMatch?.final_unit_cost,
      inventoryMatch?.finalUnitCost,
      inventoryMatch?.landed_unit_price,
      inventoryMatch?.landedUnitPrice,
      item.purchase_price,
      item.purchasePrice,
      item.cost_price,
      item.costPrice,
      item.buying_price,
      item.buyingPrice,
      item.unit_cost,
      item.unitCost,
      item.unit_price ||
        item.unitPrice,
      item.landing_cost,
      item.landingCost,
      item.price,
      item.dprice,
      item.selling_price,
      inventoryMatch?.purchase_price,
      inventoryMatch?.purchasePrice,
      inventoryMatch?.cost_price,
      inventoryMatch?.costPrice,
      inventoryMatch?.buying_price,
      inventoryMatch?.buyingPrice,
      inventoryMatch?.unit_cost,
      inventoryMatch?.unitCost,
      inventoryMatch?.unit_price,
      inventoryMatch?.unitPrice,
      inventoryMatch?.landing_cost,
      inventoryMatch?.landingCost,
      inventoryMatch?.price,
      inventoryMatch?.dprice,
      inventoryMatch?.selling_price
    );

    if (price > 0) return Math.ceil(price);

    const totalAmount = firstNumber(
      item.landed_purchase_amount,
      item.landedPurchaseAmount,
      item.final_cost,
      item.finalCost,
      inventoryMatch?.landed_purchase_amount,
      inventoryMatch?.landedPurchaseAmount,
      inventoryMatch?.final_cost,
      inventoryMatch?.finalCost,
      item.total_purchase_amount,
      item.totalPurchaseAmount,
      item.purchase_amount,
      item.purchaseAmount,
      item.total_cost,
      item.totalCost,
      inventoryMatch?.total_purchase_amount,
      inventoryMatch?.totalPurchaseAmount,
      inventoryMatch?.purchase_amount,
      inventoryMatch?.purchaseAmount,
      inventoryMatch?.total_cost,
      inventoryMatch?.totalCost
    );

    const availableUnits = Number(item.available_units || item.no_of_units || 0);

    if (totalAmount > 0 && availableUnits > 0) {
      return Math.ceil(totalAmount / availableUnits);
    }

    return 0;
  };

  const getPackingBarcodeMatches = (item) => {
    const productId = String(item.product_id || item.catalog_product_id || '');
    const sourceBarcodeId = String(item.product_barcode_id || item.catalog_product_barcode_id || '');
    const productName = normalizeText(item.product_name);
    const productNameWithoutBrand = normalizeText(
      String(item.product_name || '').replace(item.brand_name_english || item.brand_name || '', '')
    );
    const productNameWithoutCode = normalizeText(
      String(item.product_name || '')
        .replace(/^[A-Z]+\d+\s*-\s*/i, '')
        .replace(item.brand_name_english || item.brand_name || '', '')
    );
    const brandName = normalizeText(item.brand_name_english || item.brand_name);
    const categoryName = normalizeText(item.category_name_english || item.category_name);

    return catalogBarcodes
      .filter((barcode) => {
        const barcodeProductId = String(
          barcode.product_id || barcode.catalog_product_id || ''
        );
        const sameProductId = productId && barcodeProductId === productId;
        const barcodeId = String(barcode.id || barcode.product_barcode_id || '');
        const barcodeName = normalizeText(
            barcode.product_name_eng ||
              barcode.product_name ||
              barcode.name ||
              barcode.productName
          );
        const sameName =
          productName &&
          (barcodeName === productName ||
            (productNameWithoutBrand && barcodeName === productNameWithoutBrand) ||
            (productNameWithoutCode && barcodeName === productNameWithoutCode) ||
            (!barcodeName && sameProductId));
        const sameBrand =
          !brandName ||
          !(
            barcode.brand_name_english ||
            barcode.brand_name ||
            barcode.brand
          ) ||
          normalizeText(
            barcode.brand_name_english || barcode.brand_name || barcode.brand
          ) === brandName;
        const sameCategory =
          !categoryName ||
          !(
            barcode.category_name_english ||
            barcode.category_name ||
            barcode.category
          ) ||
          normalizeText(
            barcode.category_name_english ||
              barcode.category_name ||
              barcode.category
          ) === categoryName;

        if (productId) return sameProductId && barcodeId !== sourceBarcodeId;

        return sameName && sameBrand && sameCategory && barcodeId !== sourceBarcodeId;
      })
      .sort((a, b) => getItemUnitQtyInGrams(a) - getItemUnitQtyInGrams(b));
  };

  const getDefaultPackUnitId = () => {
    const preferred = (units || []).find((unit) => {
      const text = normalizeUnit(
        [unit.unit_short_code, unit.unit_code, unit.unit_name].filter(Boolean).join(' ')
      );
      return text.includes('gms') || text === 'gm' || text === 'g';
    });

    return preferred?.id || '';
  };

  const getUnitById = (unitId) =>
    (units || []).find((unit) => String(unit.id) === String(unitId)) || {};

  const findUnitByCode = (code) => {
    const normalizedCode = normalizeUnit(code);
    return (
      (units || []).find((unit) => {
        const unitText = normalizeUnit(
          [unit.unit_short_code, unit.unit_code, unit.unit_name].filter(Boolean).join(' ')
        );
        if (normalizedCode.startsWith('kg')) return unitText.startsWith('kg');
        if (normalizedCode.startsWith('gm') || normalizedCode === 'g') {
          return unitText.startsWith('gm') || unitText.startsWith('gms') || unitText === 'g';
        }
        return unitText === normalizedCode;
      }) || {}
    );
  };

  const getPackSizeFromText = (text) => {
    const match = String(text || '').match(/([0-9]+(?:\.[0-9]+)?)\s*(kg|kgs|gms|gm|g)\b/i);
    if (!match) return null;

    const unit = findUnitByCode(match[2]);
    return {
      quantity: Number(match[1]),
      unit_id: unit.id || '',
      unit_short_code: unit.unit_short_code || match[2].toUpperCase(),
      unit_name: unit.unit_name || unit.unit_short_code || match[2].toUpperCase(),
    };
  };

  const getPackingProductName = (name, brandName = '') => {
    const brand = String(brandName || '').trim();
    let text = String(name || '');

    if (brand) {
      text = text.replace(new RegExp(`^${brand.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s+`, 'i'), '');
    }

    return text
      .replace(/\b\d+(?:\.\d+)?\s*(kg|kgs|gms|gm|g)\s*(bag|pack|pkt|packet|sack)?\b/gi, '')
      .replace(/\b(bag|pack|pkt|packet|sack)\b/gi, '')
      .replace(/\s*-\s*$/g, '')
      .replace(/\s{2,}/g, ' ')
      .trim();
  };

  const updateDynamicPackForm = (itemIndex, field, value) => {
    setDynamicPackForms((prev) => ({
      ...prev,
      [itemIndex]: {
        quantity: '',
        unit_id: getDefaultPackUnitId(),
        gst_rate: 0,
        margin_percentage: 0,
        labour_percentage: 0,
        transport_percentage: 0,
        load_percentage: 0,
        unload_percentage: 0,
        ...(prev[itemIndex] || {}),
        [field]: value,
      },
    }));
  };

  const getConfiguredSourceUnits = (item) => {
    const sourceGrams = isWeightUnit(item)
      ? getItemUnitQtyInGrams(item)
      : getSourcePackBaseUnits(item);

    if (!sourceGrams && !isWeightUnit(item)) return Number(item.no_of_units || item.qty || 0);

    if (!sourceGrams) return Number(item.no_of_units || 0);

    const configuredGrams = (item.packing_configs || []).reduce(
      (sum, config) =>
        sum +
        getItemUnitQtyInGrams(config) * Number(config.pack_count || 0),
      0
    );

    if (!configuredGrams) return 0;

    return Number((configuredGrams / sourceGrams).toFixed(3));
  };

  const getConfiguredPackGrams = (item) =>
    (item.packing_configs || []).reduce(
      (sum, config) =>
        sum + getItemUnitQtyInGrams(config) * Number(config.pack_count || 0),
      0
    );

  const getRemainingSourceUnits = (item) =>
    Number(item.available_units || 0) - getConfiguredSourceUnits(item);

  const getPackingUsageText = (item) => {
    if (!(item.packing_configs || []).length) {
      return `No packing selected | Available: ${formatDecimal(
        item.available_units
      )} x ${formatPackUnit(item)}`;
    }

    return `Used: ${formatDecimal(getConfiguredSourceUnits(item))} x ${formatPackUnit(
      item
    )} (${formatDecimal(getConfiguredPackGrams(item), 0)} GMS) | Remaining: ${formatDecimal(
      getRemainingSourceUnits(item)
    )} x ${formatPackUnit(item)} | Available: ${formatDecimal(item.available_units)}`;
  };

  const getLooseQtyPerUnit = (item) => {
    const qty = Number(item.barcode_quantity || item.quantity || item.catalog_quantity || 0);
    const unit = normalizeUnit(item.unit_short_code || item.unit_name || item.units || item.unit);

    return unit === 'qty' && qty > 1 ? qty : 0;
  };

  const getRatePlansForConfig = (config) =>
    ratePlansByBarcodeId.get(String(config.product_barcode_id)) || [];

  const applyRatePlanToConfig = (config, ratePlan) => ({
    ...config,
    rate_plan_id: ratePlan?.id || '',
    rate_for: ratePlan?.rate_for || '',
    gst_rate: ratePlan?.gst_rate || '',
    margin_percentage: ratePlan?.margin_percentage || '',
    labour_percentage: ratePlan?.labour_percentage || '',
    transport_percentage: ratePlan?.transport_percentage || '',
    load_percentage: ratePlan?.load_percentage || '',
    unload_percentage: ratePlan?.unload_percentage || '',
    notes: ratePlan?.notes || config.notes || null,
  });

  const getDispatchUnitsFromEntry = (item) => {
    if (isInternalPacking) return getConfiguredSourceUnits(item);

    const looseQtyPerUnit = getLooseQtyPerUnit(item);
    if (item.dispatch_entry_mode === 'qty' && looseQtyPerUnit) {
      return Number((Number(item.dispatch_qty || 0) / looseQtyPerUnit).toFixed(3));
    }

    return Number(item.no_of_units || 0);
  };

  const getSourceUnitsForPayload = (item) => getDispatchUnitsFromEntry(item);

  const getPackingSummary = (item) =>
    (item.packing_configs || [])
      .map((config) =>
        [
          `${config.product_name || item.product_name} ${formatPackUnit(config)}`,
          `x ${Number(config.pack_count || 0)}`,
        ].join(' ')
      )
      .join('; ');

  const inventoryRows = useMemo(() => {
    return inventoryProducts
      .filter((item) => {
        const availableUnits = Number(item.count_in_stock ?? item.no_of_units ?? 0);
        const sameWarehouse =
          !sourceWarehouseId ||
          String(item.warehouse_id) === String(sourceWarehouseId);
        const entityType = String(item.business_entity_type || '').toLowerCase();
        const isPackingStock =
          entityType.includes('internal_packing') ||
          entityType.includes('packing');
        const hidePackedStockAsSource = isInternalPacking && isPackingStock;

        return (
          (isInternalPacking ? availableUnits > 0 : availableUnits >= 0) &&
          sameWarehouse &&
          item.is_active !== false &&
          item.product_barcode_id &&
          !hidePackedStockAsSource
        );
      })
      .map((item) => {
        const expDateOnly =  item.exp_date
        const catalogBarcode = catalogBarcodes.find(
          (barcode) =>
            String(barcode.id || barcode.product_barcode_id) ===
            String(item.product_barcode_id)
        ) || {};
        const mkBarcode =
          item.mk_barcode || item.bar_code || catalogBarcode.mk_barcode || '';
        const vendorBarcode =
          item.vendor_barcode || catalogBarcode.barcode ||
          (item.barcode && String(item.barcode) !== String(mkBarcode) ? item.barcode : '');

        return {
          ...item,
          inventory_product_id: item.id,
          exp_date_only: expDateOnly,
          available_units: Number(item.count_in_stock ?? item.no_of_units ?? 0),
          product_barcode_id: item.product_barcode_id,
          mk_barcode: mkBarcode,
          vendor_barcode: vendorBarcode,
          barcode: vendorBarcode,
          display_barcode: mkBarcode,
          image_url: item.image_url || item.imageUrl || '',
          display_product_name: getDispatchItemProductName(item),
          searchText: [
            item.product_code,
            getDispatchItemProductName(item),
            item.product_name,
            item.product_name_eng,
            item.product_name_tel,
            mkBarcode,
            vendorBarcode,
            item.bar_code,
            getDispatchItemBrand(item),
            item.brand_name_english,
            item.category_name_english,
            item.unit_name,
            item.unit_short_code,
            expDateOnly,
          ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase(),
        };
      });
  }, [inventoryProducts, sourceWarehouseId, isInternalPacking, catalogBarcodes]);

  // Keep cart quantities the user entered, but refresh the available balance
  // whenever published stock or a server refresh changes inventory.
  useEffect(() => {
    setItems(previous => {
      let changed = false;
      const next = previous.map(item => {
        const stock = inventoryRows.find(row => String(row.inventory_product_id) === String(item.inventory_product_id));
        const available = stock ? stock.available_units : 0;
        if (Number(item.available_units) === available) return item;
        changed = true;
        return { ...item, available_units: available };
      });
      return changed ? next : previous;
    });
  }, [inventoryRows]);

  const suggestions = useMemo(() => {
    const value = search.trim().toLowerCase();

    if (!sourceWarehouseId) return [];
    // Zero-stock batches are never offered.
    const rows = inventoryRows.filter((row) => row.available_units > 0 && (!value || row.searchText.includes(value)));
    return groupInventorySuggestions(rows);
  }, [inventoryRows, search, sourceWarehouseId]);
  // Catalog packs matching the search with no stock in this warehouse (stock
  // from before the application). zeroRow = existing inventory row at 0 stock.
  const missingMatches = useMemo(() => {
    const value = search.trim().toLowerCase();
    if (!sourceWarehouseId || isInternalPacking || value.length < 2) return [];
    const inStock = new Set(inventoryRows.filter(row => row.available_units > 0).map(row => String(row.product_barcode_id)));
    const zeroRows = new Map();
    inventoryRows.filter(row => !(row.available_units > 0)).forEach(row => {
      if (!zeroRows.has(String(row.product_barcode_id))) zeroRows.set(String(row.product_barcode_id), row);
    });
    const matches = new Map();
    // Zero-stock batches already in inventory.
    zeroRows.forEach((row, barcodeId) => {
      if (inStock.has(barcodeId) || !row.searchText.includes(value)) return;
      const pack = catalogBarcodes.find(item => String(item.id) === barcodeId) || {
        id: row.product_barcode_id, product_name_eng: row.display_product_name, product_code: row.product_code,
        brand_name_english: row.brand_name_english, quantity: row.barcode_quantity || row.quantity, unit_short_code: row.unit_short_code };
      matches.set(barcodeId, { pack, zeroRow: row });
    });
    // Catalog packs with no inventory row in this warehouse.
    catalogBarcodes.forEach(pack => {
      const barcodeId = String(pack.id);
      if (matches.has(barcodeId) || inStock.has(barcodeId) || zeroRows.has(barcodeId) || pack.is_active === false) return;
      if (![pack.product_name_eng, pack.product_name_tel, pack.product_code, pack.mk_barcode, pack.barcode, pack.brand_name_english]
        .filter(Boolean).join(' ').toLowerCase().includes(value)) return;
      matches.set(barcodeId, { pack, zeroRow: null });
    });
    return [...matches.values()].slice(0, 10);
  }, [search, sourceWarehouseId, isInternalPacking, inventoryRows, catalogBarcodes]);
  // Opening-stock entry: { pack } for an existing catalog pack, { name } for a new product.
  const [stockForm, setStockForm] = useState(null);
  const [pendingAdd, setPendingAdd] = useState(null);

  // Product line whose panel is open: { key, mode: 'available' | 'custom' }.
  const [expandedSuggestion, setExpandedSuggestion] = useState(null);
  const [customEntry, setCustomEntry] = useState({ amount: '', unit: 'gms' });
  const [repackEntry, setRepackEntry] = useState({ sourceId: '', packs: '1' });
  const [repacking, setRepacking] = useState(false);
  const [repackMessage, setRepackMessage] = useState('');
  // Packed stock created by an automatic repack, waiting for the inventory
  // refresh before it is added to this dispatch: [{ id, units }].
  const [pendingPacked, setPendingPacked] = useState([]);
  // Inventory id whose custom pack form should receive focus once rendered.
  const [customFocusId, setCustomFocusId] = useState(null);
  useEffect(() => {
    if (customFocusId == null) return;
    const input = document.querySelector(`[data-custom-pack-qty="${customFocusId}"]`);
    if (!input) return;
    input.scrollIntoView?.({ block: 'center' });
    input.focus();
    setCustomFocusId(null);
  }, [customFocusId, items]);

  const getDestinationLabel = (item) =>
    getDispatchDestinationLabel(item, destinationType);

  const getAlreadyAddedUnits = (row) => {
    return items
      .filter(
        (item) =>
          String(item.inventory_product_id) === String(row.inventory_product_id)
      )
      .reduce((sum, item) => sum + Number(item.no_of_units || 0), 0);
  };

  const makeInventoryItem = (inventoryRow, initialUnits, availableUnits) => {
    return {
      inventory_product_id: Number(inventoryRow.inventory_product_id),
      product_barcode_id: Number(inventoryRow.product_barcode_id),
      dispatch_margin_percent: 10,
      unit_mrp: inventoryRow.unit_mrp ?? inventoryRow.unit_MRP ?? inventoryRow.mrp_amount ?? inventoryRow.mrpAmount ?? inventoryRow.mrp ?? inventoryRow.MRP,
      source_unit_price: calculatePackPricingFromRow(inventoryRow).costPerPiece || '',

      mk_barcode: inventoryRow.mk_barcode || inventoryRow.bar_code || '',
      vendor_barcode: inventoryRow.vendor_barcode || inventoryRow.barcode || '',
      barcode: inventoryRow.vendor_barcode || inventoryRow.barcode || '',
      barcode_quantity: inventoryRow.barcode_quantity || '',
      mkid: inventoryRow.mkid || inventoryRow.MKID || inventoryRow.sku_id || '',
      image_url: inventoryRow.image_url || inventoryRow.imageUrl || '',
      imageUrl: inventoryRow.image_url || inventoryRow.imageUrl || '',
      product_id: inventoryRow.product_id || inventoryRow.catalog_product_id || '',
      purchase_price: inventoryRow.purchase_price,
      purchasePrice: inventoryRow.purchasePrice,
      cost_price: inventoryRow.cost_price,
      costPrice: inventoryRow.costPrice,
      buying_price: inventoryRow.buying_price,
      buyingPrice: inventoryRow.buyingPrice,
      unit_cost: inventoryRow.unit_cost,
      unitCost: inventoryRow.unitCost,
      unit_price: inventoryRow.unit_price,
      unitPrice: inventoryRow.unitPrice,
      landing_cost: inventoryRow.landing_cost,
      landingCost: inventoryRow.landingCost,
      price: inventoryRow.price,
      mrp_amount: inventoryRow.mrp_amount || inventoryRow.mrpAmount,
      mrpAmount: inventoryRow.mrpAmount || inventoryRow.mrp_amount,
      dprice: inventoryRow.dprice,
      discounted_price: inventoryRow.discounted_price || inventoryRow.discountedPrice,
      configured_discount_percent:
        inventoryRow.configured_discount_percent || inventoryRow.discount_percent,
      discount_percent:
        inventoryRow.discount_percent || inventoryRow.configured_discount_percent,
      selling_price: inventoryRow.selling_price,
      landed_purchase_unit_price: inventoryRow.landed_purchase_unit_price,
      landedPurchaseUnitPrice: inventoryRow.landedPurchaseUnitPrice,
      final_unit_cost: inventoryRow.final_unit_cost,
      finalUnitCost: inventoryRow.finalUnitCost,
      landed_unit_price: inventoryRow.landed_unit_price,
      landedUnitPrice: inventoryRow.landedUnitPrice,
      total_purchase_amount: inventoryRow.total_purchase_amount,
      totalPurchaseAmount: inventoryRow.totalPurchaseAmount,
      purchase_amount: inventoryRow.purchase_amount,
      purchaseAmount: inventoryRow.purchaseAmount,
      total_cost: inventoryRow.total_cost,
      totalCost: inventoryRow.totalCost,
      landed_purchase_amount: inventoryRow.landed_purchase_amount,
      landedPurchaseAmount: inventoryRow.landedPurchaseAmount,
      final_cost: inventoryRow.final_cost,
      finalCost: inventoryRow.finalCost,

      product_code: inventoryRow.product_code,
      product_name: getDispatchItemProductName(inventoryRow),
      brand_id: inventoryRow.brand_id || inventoryRow.catalog_brand_id || '',
      category_id: inventoryRow.category_id || inventoryRow.catalog_category_id || '',
      unit_id: inventoryRow.unit_id || inventoryRow.catalog_unit_id || '',

      brand_name_english: getDispatchItemBrand(inventoryRow),
      category_name_english: inventoryRow.category_name_english,
      unit_short_code: inventoryRow.unit_short_code,
      unit_name: inventoryRow.unit_name,
      source_pack_quantity:
        getPackSizeFromText(
          [
            inventoryRow.product_name,
            inventoryRow.product_name_eng,
            inventoryRow.display_product_name,
            inventoryRow.product_code,
          ].join(' ')
        )?.quantity || '',
      source_pack_unit_id:
        getPackSizeFromText(
          [
            inventoryRow.product_name,
            inventoryRow.product_name_eng,
            inventoryRow.display_product_name,
            inventoryRow.product_code,
          ].join(' ')
        )?.unit_id || '',
      source_pack_unit_short_code:
        getPackSizeFromText(
          [
            inventoryRow.product_name,
            inventoryRow.product_name_eng,
            inventoryRow.display_product_name,
            inventoryRow.product_code,
          ].join(' ')
        )?.unit_short_code || '',

      exp_date: inventoryRow.exp_date_only,
      needs_expiry: !inventoryRow.exp_date_only,
      available_units: availableUnits,

      qty: initialUnits,
      no_of_units: initialUnits,
      dispatch_entry_mode: 'units',
      dispatch_qty: '',
      notes: '',
      packing_configs: [],
    };

  };

  const addInventoryItem = (inventoryRow, { keepOpen = false, units = null } = {}) => {
    if (!sourceWarehouseId) {
      alert('Please select source warehouse first');
      return;
    }

    const availableUnits = Number(
      inventoryRow.available_units || inventoryRow.no_of_units || 0
    );

    const alreadyAddedUnits = getAlreadyAddedUnits(inventoryRow);
    const remainingUnits = availableUnits - alreadyAddedUnits;
    const initialUnits = units != null ? Math.min(Number(units), Number(remainingUnits.toFixed(3)))
      : remainingUnits >= 1 ? 1 : Number(remainingUnits.toFixed(3));

    if (initialUnits <= 0) {
      alert(`Only ${availableUnits} units available.`);
      return;
    }

    const newItem = makeInventoryItem(inventoryRow, initialUnits, availableUnits);

    setItems((prev) => {
      const existingIndex = prev.findIndex(
        (item) =>
          String(item.inventory_product_id) ===
          String(newItem.inventory_product_id)
      );

        if (existingIndex !== -1) {
          return prev.map((item, index) =>
            index === existingIndex
              ? {
                  ...item,
                  qty: Number(item.qty || 0) + initialUnits,
                  no_of_units: Number(item.no_of_units || 0) + initialUnits,
                  dispatch_entry_mode: item.dispatch_entry_mode || 'units',
                }
              : item
        );
      }

      return [...prev, newItem];
    });

    if (keepOpen) return;
    setSearch('');
    setShowSuggestions(false);
    setExpandedSuggestion(null);
  };

  const toggleSuggestionPanel = (group, mode) => {
    setExpandedSuggestion(previous => previous?.key === group.key && previous.mode === mode ? null : { key: group.key, mode });
    if (mode === 'custom') {
      setCustomEntry({ amount: '', unit: packGrams(group.rows[0]) != null ? 'gms' : 'units' });
      setRepackEntry({ sourceId: String(getRepackSources(group)[0]?.inventory_product_id || ''), packs: '1' });
    }
  };

  // Available: every in-stock batch with all of its remaining units.
  const addAllAvailable = (group) => {
    const rows = group.rows.filter(row => Number(row.available_units) - getAlreadyAddedUnits(row) > 0);
    if (!rows.length) { alert('No stock available for this product.'); return; }
    rows.forEach(row => addInventoryItem(row, { units: Number(row.available_units) - getAlreadyAddedUnits(row) }));
  };

  // Source packs that can be repacked: in stock, bulk stock before packed stock.
  const getRepackSources = (group) => {
    const isPackedStock = row => String(row.business_entity_type || '').toLowerCase().includes('packing');
    const inStock = group.rows.filter(row => Number(row.available_units) > 0);
    return [...inStock.filter(row => !isPackedStock(row)), ...inStock.filter(isPackedStock)];
  };

  const describeRepack = (group) => {
    const source = group.rows.find(row => String(row.inventory_product_id) === String(repackEntry.sourceId));
    const packGramsEach = (customEntry.unit === 'kg' ? 1000 : 1) * Number(customEntry.amount || 0);
    const sourceGramsEach = source ? packGrams(source) : null;
    if (!source || !(packGramsEach > 0) || !sourceGramsEach) return null;
    const packs = Math.floor(Number(repackEntry.packs || 0));
    const sourceUnits = Number(((packGramsEach * packs) / sourceGramsEach).toFixed(3));
    return { source, packs, sourceUnits, enough: sourceUnits <= Number(source.available_units || 0) };
  };

  // Custom: Internal Packing adds the source and prefills the custom pack.
  // Outlet/vendor/customer: repack the chosen source into custom packs right
  // away (Internal Packing order -> packed -> completed), then add the new
  // packed stock to this dispatch.
  const addCustomQuantity = async (group) => {
    const amount = Number(customEntry.amount);
    if (!(amount > 0)) { alert('Enter the custom pack quantity.'); return; }
    const unit = findUnitByCode(customEntry.unit === 'kg' ? 'kg' : 'gms');

    if (isInternalPacking) {
      const source = getRepackSources(group)[0] || group.rows[0];
      const existingIndex = items.findIndex(item => String(item.inventory_product_id) === String(source.inventory_product_id));
      addInventoryItem(source);
      setDynamicPackForms(previous => ({ ...previous, [existingIndex === -1 ? items.length : existingIndex]:
        { ...(previous[existingIndex === -1 ? items.length : existingIndex] || {}), quantity: String(amount), ...(unit.id ? { unit_id: String(unit.id) } : {}) } }));
      setCustomFocusId(String(source.inventory_product_id));
      return;
    }

    const plan = describeRepack(group);
    if (!plan) { alert('Choose the source pack to repack from. Custom packs need a source sold by weight (KG/GMS).'); return; }
    if (!(plan.packs > 0)) { alert('Enter how many packs to make.'); return; }
    if (!plan.enough) { alert(`Not enough source stock: needs ${plan.sourceUnits} × ${formatPackUnit(plan.source)}, available ${formatDecimal(plan.source.available_units)}.`); return; }
    if (!plan.source.exp_date_only) { alert('The source pack has no expiry date. Add it in inventory before repacking.'); return; }
    if (!unit.id) { alert('GMS/KG unit is not configured.'); return; }
    if (!window.confirm(`Repack ${plan.sourceUnits} × ${formatPackUnit(plan.source)} (Inv ${plan.source.inventory_product_id}) into ${plan.packs} × ${amount} ${customEntry.unit.toUpperCase()} and add them to this dispatch?`)) return;

    const base = makeInventoryItem(plan.source, 0, Number(plan.source.available_units || 0));
    const packItem = { ...base, packing_configs: [makeDynamicPackConfig(base, amount, unit.id, plan.packs)] };
    const internalDestination = destinationType === 'internal_packing' ? selectedDestination : { id: 'internal-packing', name: 'Internal Packing Dept' };
    const fail = (step, result) => { throw new Error(`${step}: ${result?.payload || result?.error?.message || 'no response'}`); };

    setRepacking(true); setRepackMessage('');
    try {
      const created = await dispatch(createInventoryDispatchOrder({
        source: `warehouse:${sourceWarehouseId}:${getWarehouseLabel(sourceWarehouse)}`,
        destination: `internal_packing:internal-packing:${getDispatchDestinationLabel(internalDestination, 'internal_packing')}`,
        warehouse_id: Number(sourceWarehouseId), source_warehouse_id: Number(sourceWarehouseId),
        expected_dispatch_at: null,
        dispatch_notes: `Auto repack for ${destinationType} dispatch${selectedDestination ? ` to ${getDestinationLabel(selectedDestination)}` : ''}`,
        dispatch_status: 'sent',
        items: [buildItemPayload(packItem, true)],
      }));
      if (!createInventoryDispatchOrder.fulfilled.match(created)) fail('Could not create the packing order', created);
      const orderId = created.payload?.id || created.payload?.order?.id;
      if (!orderId) fail('Could not create the packing order', created);
      const packed = await dispatch(updateInventoryDispatchStatus({ id: orderId, dispatch_status: 'packed' }));
      if (!updateInventoryDispatchStatus.fulfilled.match(packed)) fail(`Packing order ${created.payload?.dispatch_no || orderId} was created but could not be marked packed`, packed);
      const done = await dispatch(completeInternalPackingDispatch({ dispatchOrderId: orderId }));
      if (!completeInternalPackingDispatch.fulfilled.match(done)) fail(`Packing order ${created.payload?.dispatch_no || orderId} is packed but could not be completed`, done);
      const moved = (done.payload?.movedItems || []).map(row => ({ id: String(row.packed_inventory_product_id), units: Number(row.packed_qty) }));
      setPendingPacked(moved);
      setRepackMessage(`Repacked into ${plan.packs} × ${amount} ${customEntry.unit.toUpperCase()} (packing order ${created.payload?.dispatch_no || orderId}). Adding the packs to this dispatch…`);
      setExpandedSuggestion(null);
      dispatch(fetchInventoryProducts());
      dispatch(fetchInventoryDispatchOrders());
      dispatch(fetchStockTransactions());
    } catch (error) {
      setRepackMessage(`${error.message}. Finish it from the Dispatch Orders list if needed.`);
    } finally {
      setRepacking(false);
    }
  };

  // Add the new packed stock once the refreshed inventory includes it.
  useEffect(() => {
    if (!pendingPacked.length) return;
    const ready = pendingPacked.map(pick => ({ pick, row: inventoryRows.find(row => String(row.inventory_product_id) === pick.id) }));
    if (ready.some(({ pick, row }) => !row || Number(row.available_units) < pick.units)) return;
    setPendingPacked([]);
    ready.forEach(({ pick, row }) => addInventoryItem(row, { units: pick.units, keepOpen: true }));
    setRepackMessage(previous => previous.replace('Adding the packs to this dispatch…', 'The packs are in the dispatch list below.'));
  }, [inventoryRows, pendingPacked]); // eslint-disable-line react-hooks/exhaustive-deps

  // Add a zero-stock batch; Create Dispatch adds the missing stock (recorded as
  // a dispatch stock adjustment) before dispatching it.
  const addWithStockAdjust = (row, units = 1) => {
    const existing = items.find(item => String(item.inventory_product_id) === String(row.inventory_product_id));
    if (existing) { alert('This product is already in the dispatch list. Change its units there.'); return; }
    setItems(previous => [...previous, { ...makeInventoryItem(row, Number(units), Number(row.available_units || 0)), auto_adjust_stock: true }]);
    setSearch(''); setShowSuggestions(false);
  };

  const openStockForm = (form) => { setStockForm(form); setShowSuggestions(false); };

  // Opening stock was purchased into inventory; add it once the refresh has it.
  const handleOpeningStockCreated = ({ inventoryProductId, units, message }) => {
    setStockForm(null);
    setSearch('');
    setRepackMessage(message);
    setPendingAdd({ id: inventoryProductId, units });
    dispatch(fetchInventoryProducts());
    dispatch(fetchStockTransactions());
  };

  useEffect(() => {
    if (!pendingAdd) return;
    const row = inventoryRows.find(item => String(item.inventory_product_id) === pendingAdd.id);
    if (!row || Number(row.available_units) < pendingAdd.units) return;
    setPendingAdd(null);
    addInventoryItem(row, { units: pendingAdd.units });
  }, [inventoryRows, pendingAdd]); // eslint-disable-line react-hooks/exhaustive-deps

  const scannedRows = useMemo(() => {
    const barcode = search.trim();
    if (!sourceWarehouseId || !barcode) return [];
    return inventoryRows.filter(row => row.available_units > 0 && [row.mk_barcode, row.vendor_barcode]
      .some(value => value && String(value).trim() === barcode));
  }, [search, sourceWarehouseId, inventoryRows]);

  useEffect(() => {
    if (scannedRows.length > 1) {
      const key = groupInventorySuggestions(scannedRows, 1)[0]?.key;
      setExpandedSuggestion(previous => (previous?.key === key && previous.mode === 'available') || !key ? previous : { key, mode: 'available' });
    }
  }, [scannedRows]);

  const addScannedItem = useRef(null);
  useEffect(() => {
    addScannedItem.current = addInventoryItem;
  });

  useEffect(() => {
    if (scannedRows.length !== 1) return;
    // Scanners without an Enter suffix should also add the completed barcode.
    const timer = setTimeout(() => {
      setSearch('');
      addScannedItem.current(scannedRows[0]);
    }, 200);
    return () => clearTimeout(timer);
  }, [scannedRows]);

  const importDispatchCsv = (rows) => {
    if (!sourceWarehouse || !selectedDestination) throw new Error('Select source warehouse and destination first.');
    const imported = new Map();
    rows.forEach((row, index) => {
      try {
        const stock = inventoryRows.find(s => String(s.inventory_product_id) === row.inventory_product_id);
        if (!stock) throw new Error('Inventory ID is unavailable in this source warehouse.');
        if (items.some(item => String(item.inventory_product_id) === row.inventory_product_id)) throw new Error('This inventory ID is already in the review table. Remove it before importing.');
        let item = imported.get(row.inventory_product_id);
        if (!item) { item = makeInventoryItem(stock, 0, stock.available_units); imported.set(row.inventory_product_id, item); }
        if (isInternalPacking) {
          const pack = getPackingBarcodeMatches(item).find(p => String(p.id || p.product_barcode_id) === row.packing_barcode_id);
          if (!pack) throw new Error('Packing barcode must be an existing compatible pack for this product.');
          if (item.packing_configs.some(p => String(p.product_barcode_id) === row.packing_barcode_id)) throw new Error('Duplicate packing barcode for this inventory ID.');
          if (row.source_pack_quantity) item.source_pack_quantity = csvNumber(row.source_pack_quantity,'source_pack_quantity');
          if (row.source_pack_unit_id) {
            const unit = units.find(u => String(u.id) === row.source_pack_unit_id);
            if (!unit) throw new Error('Unknown source_pack_unit_id.');
            item.source_pack_unit_id = unit.id; item.source_pack_unit_short_code = unit.unit_short_code;
          }
          item.packing_configs.push({ ...pack, product_barcode_id:Number(pack.id || pack.product_barcode_id), barcode_quantity:pack.quantity || pack.barcode_quantity, pack_count:csvNumber(row.pack_count,'pack_count'), product_name:pack.product_name_eng || item.product_name });
          if (!isWeightUnit(item) && (!item.source_pack_quantity || !item.source_pack_unit_id)) throw new Error('Source pack quantity and unit ID required for this stock.');
          item.no_of_units = getConfiguredSourceUnits(item); item.qty = item.no_of_units;
        } else {
          if (item.no_of_units > 0) throw new Error('Duplicate inventory ID. Combine units into one CSV row.');
          item.no_of_units = csvNumber(row.no_of_units,'no_of_units'); item.qty = item.no_of_units;
          if (row.margin_percent) item.dispatch_margin_percent = csvNumber(row.margin_percent,'margin_percent',true);
          item.auto_adjust_stock = true;
        }
        item.notes = row.notes || '';
        if (item.no_of_units <= 0 || (isInternalPacking && item.no_of_units > stock.available_units)) throw new Error('Requested units exceed available stock or pack conversion is invalid.');
        if (!isInternalPacking && getCurrentDispatchPricing(item).pricingError) throw new Error(getCurrentDispatchPricing(item).pricingError);
        if (!isInternalPacking && getCurrentDispatchPricing(item).exceedsMrp) throw new Error('Dispatch price exceeds MRP. Reduce margin_percent.');
      } catch (error) { throw new Error(`Row ${index+2}: ${error.message}`); }
    });
    setItems(prev => [...prev, ...imported.values()]);
  };

  const updateItem = (index, field, value) => {
    setItems((prev) =>
      prev.map((item, itemIndex) => {
        if (itemIndex !== index) return item;

        if (field === 'no_of_units' || field === 'qty') {
          const nextUnits = Number(value || 0);

          if (!item.auto_adjust_stock && nextUnits > Number(item.available_units || 0)) {
            alert(`Only ${Number(item.available_units || 0)} units available.`);
            return item;
          }

          return {
            ...item,
            qty: nextUnits,
            no_of_units: nextUnits,
          };
        }

        if (field === 'dispatch_entry_mode') {
          return {
            ...item,
            dispatch_entry_mode: value,
            dispatch_qty: '',
            qty: value === 'qty' ? 0 : Number(item.no_of_units || 1),
            no_of_units: value === 'qty' ? 0 : Number(item.no_of_units || 1),
          };
        }

        if (field === 'dispatch_qty') {
          const looseQtyPerUnit = getLooseQtyPerUnit(item);
          const nextQty = Number(value || 0);
          const maxQty = Number(item.available_units || 0) * looseQtyPerUnit;

          if (!item.auto_adjust_stock && looseQtyPerUnit && nextQty > maxQty) {
            alert(`Only ${formatDecimal(maxQty, 0)} qty available.`);
            return item;
          }

          const nextUnits = looseQtyPerUnit
            ? Number((nextQty / looseQtyPerUnit).toFixed(3))
            : nextQty;

          return {
            ...item,
            dispatch_qty: value,
            qty: nextUnits,
            no_of_units: nextUnits,
          };
        }

        return { ...item, [field]: value };
      })
    );
  };

  const addPackingConfig = (itemIndex, barcodeConfig) => {
    setItems((prev) =>
      prev.map((item, index) => {
        if (index !== itemIndex) return item;

        const alreadyAdded = (item.packing_configs || []).some(
          (config) =>
            String(config.product_barcode_id) ===
            String(barcodeConfig.id || barcodeConfig.product_barcode_id)
        );

        if (alreadyAdded) return item;

        const newConfig = {
          product_barcode_id: Number(
            barcodeConfig.id || barcodeConfig.product_barcode_id
          ),
          mk_barcode:
            barcodeConfig.mk_barcode ||
            barcodeConfig.mkBarcode ||
            barcodeConfig.MKBarcode ||
            barcodeConfig.bar_code ||
            barcodeConfig.barcode ||
              '',
          bar_code: barcodeConfig.bar_code || '',
          barcode: barcodeConfig.barcode || barcodeConfig.bar_code || '',
          product_name:
            getPackingProductName(
              barcodeConfig.product_name_eng || barcodeConfig.product_name || item.product_name,
              barcodeConfig.brand_name_english ||
                barcodeConfig.brand_name ||
                item.brand_name_english ||
                item.brand_name
            ),
          barcode_quantity:
            barcodeConfig.barcode_quantity || barcodeConfig.quantity || '',
          unit_short_code:
            barcodeConfig.unit_short_code ||
            barcodeConfig.unit_name ||
            barcodeConfig.units ||
            '',
          pack_count: 1,
        };

        return {
          ...item,
          packing_configs: [...(item.packing_configs || []), newConfig],
        };
      })
    );
  };

  const makeDynamicPackConfig = (item, quantity, unitId, packCount = 1) => {
    const unit = getUnitById(unitId);
    return {
      dynamic_pack: true,
      product_barcode_id: null,
      product_id: Number(item.product_id || 0),
      brand_id: Number(item.brand_id || 0),
      category_id: Number(item.category_id || 0),
      unit_id: Number(unitId),
      product_name: getPackingProductName(
        item.product_name,
        item.brand_name_english || item.brand_name
      ),
      barcode_quantity: quantity,
      quantity,
      unit_short_code: unit.unit_short_code || unit.unit_name || '',
      unit_name: unit.unit_name || unit.unit_short_code || '',
      pack_count: packCount,
      mk_barcode: makeMkBarcode({
        product_id: item.product_id,
        brand_id: item.brand_id,
        category_id: item.category_id,
        unit_id: unitId,
        quantity,
      }),
      notes: 'Auto-create barcode from internal packing',
    };
  };

  const addDynamicPackingConfig = (itemIndex, preset = null) => {
    const form = {
      quantity: '',
      unit_id: getDefaultPackUnitId(),
      ...(dynamicPackForms[itemIndex] || {}),
      ...(preset || {}),
    };
    const quantity = Number(form.quantity || 0);
    const unit = getUnitById(form.unit_id);

    if (!quantity || !form.unit_id) {
      alert('Enter pack quantity and unit.');
      return;
    }

    setItems((prev) =>
      prev.map((item, index) => {
        if (index !== itemIndex) return item;

        const alreadyAdded = (item.packing_configs || []).some(
          (config) =>
            Number(config.barcode_quantity || 0) === quantity &&
            String(config.unit_id || '') === String(form.unit_id || '')
        );

        if (alreadyAdded) return item;

        const newConfig = {
          dynamic_pack: true,
          product_barcode_id: null,
          product_id: Number(item.product_id || 0),
          brand_id: Number(item.brand_id || 0),
          category_id: Number(item.category_id || 0),
          unit_id: Number(form.unit_id),
          product_name: getPackingProductName(
            item.product_name,
            item.brand_name_english || item.brand_name
          ),
          barcode_quantity: quantity,
          quantity,
          unit_short_code: unit.unit_short_code || unit.unit_name || '',
          unit_name: unit.unit_name || unit.unit_short_code || '',
          pack_count: 1,
          mk_barcode: makeMkBarcode({
            product_id: item.product_id,
            brand_id: item.brand_id,
            category_id: item.category_id,
            unit_id: form.unit_id,
            quantity,
          }),
          notes: 'Auto-create barcode from internal packing',
          ...(preset?.preset_key ? { preset_key: preset.preset_key, notes: preset.notes } : {}),
        };

        return {
          ...item,
          packing_configs: [...(item.packing_configs || []), newConfig],
        };
      })
    );

    setDynamicPackForms((prev) => ({
      ...prev,
      [itemIndex]: {
        ...form,
        quantity: '',
      },
    }));
  };

  const getBudgetGrams = (item, budget) => {
    if (!isWeightUnit(item)) {
      const sourceUnit = normalizeUnit(item.source_pack_unit_short_code || getUnitById(item.source_pack_unit_id).unit_short_code);
      if (!sourceUnit.startsWith('kg') && !sourceUnit.startsWith('gm') && sourceUnit !== 'g') return 0;
    }
    const sourceGrams = isWeightUnit(item) ? getItemUnitQtyInGrams(item) : getSourcePackBaseUnits(item);
    const prospectivePackCount = (item.packing_configs || []).reduce((sum, row) => sum + Number(row.pack_count || 0), 0) + 1;
    return calculateStageBudgetGrams({ budget, costPerGram: getSourceUnitPrice(item) / sourceGrams,
      sourceMrp: item.unit_mrp, sourceGrams,
      repackingCost: Number(item.repacking_cost_total || 0) / prospectivePackCount,
      marketRates, ledgers: packingLedgers, context: { product_id: item.product_id, product_barcode_id: null,
        destination_type: 'internal_packing', warehouse_id: sourceWarehouseId } });
  };

  const getConfigPrices = (item, config) => {
    const sourceGrams = isWeightUnit(item) ? getItemUnitQtyInGrams(item) : getSourcePackBaseUnits(item);
    const grams = getItemUnitQtyInGrams(config);
    const count = (item.packing_configs || []).reduce((sum, row) => sum + Number(row.pack_count || 0), 0);
    return calculateStagePrice({ grams, purchaseCost: getSourceUnitPrice(item) * grams / sourceGrams,
      fixedMrp: proportionalPackMrp(item.unit_mrp, sourceGrams, grams),
      repackingCost: count > 0 ? Number(item.repacking_cost_total || 0) / count : 0,
      marketRates, ledgers: packingLedgers, context: { product_id: item.product_id,
        product_barcode_id: config.product_barcode_id || null,
        destination_type: 'internal_packing', warehouse_id: sourceWarehouseId } });
  };

  const getCurrentDispatchPricing = item => {
    const context = { product_id:item.product_id, product_barcode_id:item.product_barcode_id,
      destination_type:destinationType, dispatch_to_id: destinationType === 'internal_packing' ? null : destinationId,
      warehouse_id:sourceWarehouseId };
    const quote = calculateStagePrice({ grams: isWeightUnit(item) ? getItemUnitQtyInGrams(item) : 0,
      purchaseCost: getSourceUnitPrice(item), marketRates, ledgers: packingLedgers,
      fixedMrp: item.unit_mrp,
      context });
    if (!quote) {
      const plan = resolvePricingConfig(packingLedgers, context);
      return { ...getDispatchPricing(item), margin: plan ? Number(plan.margin_percent || 0) * Number(plan.proportion_factor ?? 1) : 0,
        price:0, exceedsMrp:false, ledgerConfigId:plan?.id,
        pricingError: plan ? `Rule #${plan.id} matches, but its price cannot be calculated. Check purchase cost and applicable market rate.`
          : explainPricingMismatch(packingLedgers, context) };
    }
    return { cost:getSourceUnitPrice(item), margin:quote.margin_percent * quote.proportion_factor,
      price:quote.price, mrp:quote.mrp, exceedsMrp:quote.mrp > 0 && quote.price > quote.mrp,
      cappedAtMrp: quote.cappedAtMrp, uncappedPrice: quote.uncappedPrice,
      discount:(1 - quote.price / quote.mrp) * 100,
      configuredDiscount: quote.discount_percent,
      ledgerConfigId:quote.ledger_config_id,
      pricingBasis:quote.pricing_basis, marketRate:quote.market_rate_per_kg };
  };

  const togglePresetPacking = (itemIndex, key, grams, checked) => {
    if (!checked) {
      setItems(previous => previous.map((item, index) => index === itemIndex
        ? { ...item, packing_configs: item.packing_configs.filter(config => config.preset_key !== key) } : item));
      return;
    }
    if (!grams) { alert('Configure matching internal packing and outlet ledger margins, or * defaults, and a valid source weight/cost.'); return; }
    const unit = grams === 1000 && key.startsWith('regular') ? findUnitByCode('kg') : findUnitByCode('gms');
    const quantity = unit.id && normalizeUnit(unit.unit_short_code || unit.unit_name).startsWith('kg') ? grams / 1000 : grams;
    if (items[itemIndex].packing_configs.some(config => Number(config.barcode_quantity) === quantity && String(config.unit_id) === String(unit.id))) {
      alert('This pack size is already added. Adjust its pack count in the table below.');
      return;
    }
    addDynamicPackingConfig(itemIndex, { quantity,
      unit_id: unit.id, preset_key: key,
      notes: key.startsWith('budget') ? `Budget pack ${key.split('-')[1]} rupees; matching ledger margins with * fallback` : 'Regular packing preset' });
  };

  const updatePackingConfig = (itemIndex, configIndex, field, value) => {
    setItems((prev) =>
      prev.map((item, index) => {
        if (index !== itemIndex) return item;

        return {
          ...item,
          packing_configs: (item.packing_configs || []).map((config, cIndex) =>
            cIndex === configIndex
              ? { ...config, [field]: value }
              : config
          ),
        };
      })
    );
  };

  const removePackingConfig = (itemIndex, configIndex) => {
    setItems((prev) =>
      prev.map((item, index) => {
        if (index !== itemIndex) return item;

        return {
          ...item,
          packing_configs: (item.packing_configs || []).filter(
            (_, cIndex) => cIndex !== configIndex
          ),
        };
      })
    );
  };

  const removeItem = (index) => {
    setItems((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
  };

  // One dispatch item as sent to the API; internal = Internal Packing order.
  const buildItemPayload = (item, internal) => {
    const units = internal ? getConfiguredSourceUnits(item) : getSourceUnitsForPayload(item);
    return {
      inventory_product_id: Number(item.inventory_product_id),
      auto_adjust_stock: !internal && item.auto_adjust_stock === true,
      product_barcode_id: Number(item.product_barcode_id),
      MK_BARCODE: item.mk_barcode || '',
      mk_barcode: item.mk_barcode || '',
      vendor_barcode: item.vendor_barcode || item.barcode || null,
      barcode: item.vendor_barcode || item.barcode || null,
      image_url: item.image_url || item.imageUrl || null,
      qty: units,
      no_of_units: units,
      exp_date: item.exp_date,
      notes: internal
        ? getPackingSummary(item) || item.notes || null
        : item.notes || null,
      mrp_amount: item.mrp_amount || item.mrpAmount || null,
      unit_mrp: item.mrp_amount || item.mrpAmount || item.unit_mrp || null,
      dprice: item.dprice || item.discounted_price || null,
      discounted_price: item.discounted_price || item.dprice || null,
      configured_discount_percent:
        item.configured_discount_percent || item.discount_percent || 0,
      discount_percent: item.discount_percent || item.configured_discount_percent || 0,
      ...(!internal ? {
        dprice: getCurrentDispatchPricing(item).price || null,
        discounted_price: getCurrentDispatchPricing(item).price || null,
        unit_mrp: getCurrentDispatchPricing(item).mrp || null,
        mrp_amount: getCurrentDispatchPricing(item).mrp || null,
        configured_margin_percent: Number(getCurrentDispatchPricing(item).margin),
        margin_percent: Number(getCurrentDispatchPricing(item).margin),
        configured_discount_percent: getCurrentDispatchPricing(item).discount,
        discount_percent: getCurrentDispatchPricing(item).discount,
      } : {}),
      source_unit_price: Number(getSourceUnitPrice(item) || 0),
      source_pack_quantity: internal
        ? Number(
            isWeightUnit(item)
              ? item.barcode_quantity || item.quantity || 0
              : item.source_pack_quantity || 0
          )
        : undefined,
      source_pack_unit_short_code: internal
        ? isWeightUnit(item)
          ? item.unit_short_code || item.unit_name || null
          : item.source_pack_unit_short_code ||
            getUnitById(item.source_pack_unit_id).unit_short_code ||
            null
        : undefined,
      packing_configurations: internal
        ? (item.packing_configs || []).map((config) => ({
            product_barcode_id: config.product_barcode_id
              ? Number(config.product_barcode_id)
              : null,
            dynamic_pack: Boolean(config.dynamic_pack),
            product_id: Number(config.product_id || item.product_id || 0),
            brand_id: Number(config.brand_id || item.brand_id || 0),
            category_id: Number(config.category_id || item.category_id || 0),
            unit_id: Number(config.unit_id || 0),
            mk_barcode: config.mk_barcode || null,
            bar_code: config.bar_code || null,
            barcode: config.barcode || config.bar_code || null,
            product_name:
              config.product_name ||
              getPackingProductName(item.product_name, item.brand_name_english || item.brand_name),
            barcode_quantity: Number(config.barcode_quantity || 0),
            unit_short_code: config.unit_short_code || null,
            pack_count: Number(config.pack_count || 0),
            source_pack_quantity: Number(
              isWeightUnit(item)
                ? item.barcode_quantity || item.quantity || 0
                : item.source_pack_quantity || 0
            ),
            source_pack_unit_short_code: isWeightUnit(item)
              ? item.unit_short_code || item.unit_name || null
              : item.source_pack_unit_short_code ||
                getUnitById(item.source_pack_unit_id).unit_short_code ||
                null,
            repacking_cost_total: Number(item.repacking_cost_total || 0),
          }))
        : undefined,
    };
  };

  const submitHandler = async (e) => {
    e.preventDefault();

    if (!sourceWarehouseId) {
      alert('Please select source warehouse');
      return;
    }

    if (!destinationId) {
      alert('Please select destination');
      return;
    }

    if (items.length === 0) {
      alert('Please add at least one product');
      return;
    }

    const missingExpiry = items.find(item => !item.exp_date);
    if (missingExpiry) {
      alert(`Enter the expiry date for ${missingExpiry.product_name || missingExpiry.mk_barcode || missingExpiry.product_barcode_id} in the Expiry column before creating dispatch.`);
      return;
    }

    const invalidItem = items.find(
      (item) =>
        !item.inventory_product_id ||
        !item.product_barcode_id ||
        !Number.isFinite(Number(getSourceUnitsForPayload(item))) ||
        Number(getSourceUnitsForPayload(item) || 0) <= 0 ||
        (!item.auto_adjust_stock && Number(getSourceUnitsForPayload(item) || 0) > Number(item.available_units || 0))
    );

    if (invalidItem) {
      alert(`Invalid dispatch quantity or stock reference for ${invalidItem.product_name || invalidItem.product_barcode_id}. Enter a quantity greater than zero and no more than ${invalidItem.available_units} available units.`);
      return;
    }

    if (isInternalPacking) {
      const invalidSourcePackItem = items.find(
        (item) =>
          !isWeightUnit(item) &&
          (!Number(item.source_pack_quantity || 0) || !item.source_pack_unit_id)
      );

      if (invalidSourcePackItem) {
        alert('Please enter Source Pack quantity and unit for QTY source items, for example 10 KG.');
        return;
      }

      const invalidPackingConfig = items.find(
        (item) =>
          !(item.packing_configs || []).length ||
          (item.packing_configs || []).some(
            (config) =>
              (!config.product_barcode_id &&
                (!config.dynamic_pack ||
                  !config.unit_id ||
                  Number(config.barcode_quantity || 0) <= 0)) ||
              Number(config.pack_count || 0) <= 0
          )
      );

      if (invalidPackingConfig) {
        alert(
          'Please add valid packing configurations for every internal packing item.'
        );
        return;
      }
    }

    if (loadingPricing || packingLedgerError) {
      alert(loadingPricing ? 'Wait for current pricing to finish loading.' : `Refresh Current Prices before creating dispatch: ${packingLedgerError}`);
      return;
    }
    const invalidPricingItem = !isInternalPacking && items.find(item => getCurrentDispatchPricing(item).pricingError);
    if (invalidPricingItem) {
      alert(getCurrentDispatchPricing(invalidPricingItem).pricingError);
      return;
    }
    const overMrpItem = !isInternalPacking && items.find((item) => getCurrentDispatchPricing(item).exceedsMrp);
    if (overMrpItem) {
      alert('Reduce the highlighted product margin so its dispatch price is below MRP before creating the dispatch.');
      return;
    }

    const payload = {
      source: `warehouse:${sourceWarehouseId}:${getWarehouseLabel(sourceWarehouse)}`,
      destination: `${destinationType}:${destinationId}:${getDestinationLabel(
        selectedDestination
      )}`,
      warehouse_id: Number(sourceWarehouseId),
      source_warehouse_id: Number(sourceWarehouseId),
      outlet_id: destinationType === 'outlet' ? Number(destinationId) : undefined,
      expected_dispatch_at: expectedDispatchAt || null,
      dispatch_notes: dispatchNotes || null,
      dispatch_status: destinationType === 'internal_packing' ? 'sent' : 'draft',
      items: items.map(item => buildItemPayload(item, isInternalPacking)),
    };

    const result = await dispatch(createInventoryDispatchOrder(payload));

    if (createInventoryDispatchOrder.fulfilled.match(result)) {
      dispatch(fetchInventoryDispatchOrders());
      dispatch(fetchInventoryProducts());
      dispatch(fetchStockTransactions());

      setSourceWarehouseId('');
      setDestinationType('outlet');
      setDestinationId('');
      setRepackMessage('');
      setExpectedDispatchAt('');
      setDispatchNotes('');
      setSearch('');
      setShowSuggestions(false);
      setItems([]);
    }
  };

  return (
    <section className="overflow-hidden rounded-xl border bg-white shadow-sm">
      <div className="border-b bg-white px-4 py-4">
        <h2 className="text-lg font-bold text-gray-900">Create Dispatch</h2>
        <button type="button" onClick={refreshPricing} disabled={loadingPricing} className="my-3 rounded-lg border px-3 py-2 text-sm">{loadingPricing ? 'Refreshing Prices...' : 'Refresh Current Prices'}</button>
        {packingLedgerError && <p role="alert" className="text-sm text-red-700">{packingLedgerError}</p>}
        {pricingWarning && <p role="status" className="text-sm text-amber-700">{pricingWarning}</p>}
        <BulkCsvImport key={destinationType + ':' + sourceWarehouseId + ':' + destinationId} name={`dispatch_${destinationType}`}
          headers={isInternalPacking ? ['inventory_product_id','packing_barcode_id','pack_count','source_pack_quantity','source_pack_unit_id','notes'] : ['inventory_product_id','no_of_units','margin_percent','notes']}
          optionalHeaders={isInternalPacking ? [] : DISPATCH_REFERENCE_HEADERS}
          onImport={importDispatchCsv} disabled={loading}
          editOptions={{
            inventory_product_id: inventoryRows.map(stock => ({ value: String(stock.inventory_product_id), label: `${stock.display_product_name} · ${formatPackUnit(stock)} · Stock ${stock.inventory_product_id} · Available: ${formatDecimal(stock.available_units)}` })),
            packing_barcode_id: catalogBarcodes.map(pack => ({ value: String(pack.id), label: `${pack.product_name_eng || pack.product_name_tel || pack.product_code} · ${pack.quantity || pack.barcode_quantity || ''} ${pack.unit_short_code || ''} · ${pack.mk_barcode || ''}` }))
          }}
          previewColumns={[
            { key: 'product', label: 'Product details', render: row => {
              const stock = inventoryRows.find(item => String(item.inventory_product_id) === row.inventory_product_id);
              if (!stock) return `Stock unavailable in selected warehouse (ID: ${row.inventory_product_id})`;
              return <div>
                <div className="font-medium">{stock.display_product_name}</div>
                <div className="text-xs text-gray-600">{[
                  getDispatchItemBrand(stock), formatPackUnit(stock),
                  stock.display_barcode ? `Barcode: ${stock.display_barcode}` : '',
                  stock.batch_id ? `Batch: ${stock.batch_id}` : '',
                  stock.exp_date_only ? `Expiry: ${String(stock.exp_date_only).slice(0, 10)}` : ''
                ].filter(Boolean).join(' · ')}</div>
              </div>;
            } },
            { key: 'available', label: 'Available units', render: row => {
              const stock = inventoryRows.find(item => String(item.inventory_product_id) === row.inventory_product_id);
              return stock ? formatDecimal(stock.available_units) : 'Unavailable';
            } },
            ...(isInternalPacking ? [
              { key: 'pack', label: 'Output product pack', render: row => {
                const pack = catalogBarcodes.find(item => String(item.id) === row.packing_barcode_id);
                return pack ? `${pack.product_name_eng || pack.product_name_tel || pack.product_code} · ${pack.quantity || pack.barcode_quantity || ''} ${pack.unit_short_code || ''}` : `Unknown pack (ID: ${row.packing_barcode_id})`;
              } },
              ...['pack_count','source_pack_quantity','source_pack_unit_id']
                .map(key => ({ key, label: key.replaceAll('_', ' '), render: row => row[key] || '—' }))
            ] : [
              { key: 'units', label: 'Dispatch units', render: row => row.no_of_units },
              { key: 'margin', label: 'Margin %', render: row => row.margin_percent || '—' }
            ]),
            { key: 'notes', label: 'Notes', render: row => row.notes || '—' }
          ]}
          help="Choose source warehouse and destination below. This CSV adds items to one dispatch. Use inventory IDs to retain the exact batch. For internal packing, use one row per output pack; source quantity/unit fields are optional for weight-based stock."
          referenceHeaders={['inventory_product_id','batch_id','display_product_name','product_barcode_id','available_units','barcode_quantity','unit_short_code','exp_date_only']}
          referenceRows={inventoryRows} />
        {isInternalPacking && <button type="button" className="mb-3 rounded border px-3 py-2" onClick={() => downloadCsv('packing_barcodes.csv', ['id','product_id','product_name_eng','quantity','unit_id','unit_short_code','mk_barcode'], catalogBarcodes)}>Download Packing Barcode Reference</button>}
        <p className="text-sm text-gray-500">
          Select source, destination and products. Bulk dispatch stock shortfalls are added to source inventory when you click Create and recorded in stock history.
        </p>
      </div>

      <form onSubmit={submitHandler} className="space-y-4 p-4">
        <div className="grid grid-cols-1 gap-3 rounded-lg border bg-slate-50 p-4 md:grid-cols-4">
          <div>
            <label className="mb-1 block text-xs font-semibold text-gray-600">
              Source Warehouse
            </label>
            <select
              value={sourceWarehouseId}
              onChange={(e) => {
                setSourceWarehouseId(e.target.value);
                setSearch('');
                setShowSuggestions(false);
                setItems([]);
              }}
              className="w-full rounded-lg border px-3 py-2"
            >
              <option value="">Select warehouse</option>
              {warehouses.map((warehouse) => (
                <option key={warehouse.id} value={warehouse.id}>
                  {warehouse.warehouse_code} - {warehouse.warehouse_name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold text-gray-600">
              Dispatch To
            </label>
            <select
              value={destinationType}
              onChange={(e) => {
                setDestinationType(e.target.value);
                setDestinationId(
                  e.target.value === 'internal_packing' ? 'internal-packing' : ''
                );
              }}
              className="w-full rounded-lg border px-3 py-2"
            >
              <option value="outlet">Outlet</option>
              <option value="internal_packing">Internal Packing Dept</option>
              <option value="vendor">Vendor</option>
              <option value="customer">Customer</option>
              <option value="warehouse">Warehouse</option>
            </select>
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold text-gray-600">
              Destination
            </label>
            <select
              value={destinationId}
              onChange={(e) => setDestinationId(e.target.value)}
              disabled={destinationType === 'internal_packing'}
              className="w-full rounded-lg border px-3 py-2"
            >
              <option value="">Select destination</option>
              {destinations.map((destination) => (
                <option key={destination.id} value={destination.id}>
                  {getDestinationLabel(destination)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold text-gray-600">
              Expected Dispatch
            </label>
            <input
              type="datetime-local"
              value={expectedDispatchAt}
              onChange={(e) => setExpectedDispatchAt(e.target.value)}
              className="w-full rounded-lg border px-3 py-2"
            />
          </div>
        </div>

        <div className="rounded-lg border bg-white p-4">
          <label className="mb-1 block text-xs font-semibold text-gray-600">
            Dispatch Notes
          </label>
          <textarea
            value={dispatchNotes}
            onChange={(e) => setDispatchNotes(e.target.value)}
            rows={2}
            className="w-full rounded-lg border px-3 py-2"
            placeholder="Optional notes"
          />
        </div>

        {repackMessage && (
          <p role="status" className="rounded-xl border border-green-300 bg-green-50 p-3 text-sm text-green-800">{repackMessage}</p>
        )}

        <div className="rounded-xl border bg-blue-50 p-4">
          <label className="mb-1 block text-sm font-bold text-gray-700">
            Add Product From Inventory
          </label>

          <div ref={inventorySearchRef} className="relative">
            <input
              value={search}
              onFocus={() => setShowSuggestions(true)}
              onKeyDown={(event) => {
                if (event.key !== 'Enter') return;
                // A scanner's Enter suffix must never submit the dispatch form.
                event.preventDefault();
                event.stopPropagation();
                if (!event.repeat && scannedRows.length === 1) {
                  setSearch('');
                  addInventoryItem(scannedRows[0]);
                }
              }}
              onChange={(e) => {
                setSearch(e.target.value);
                setShowSuggestions(true);
              }}
              disabled={!sourceWarehouseId}
              placeholder={
                sourceWarehouseId
                  ? 'Search by barcode, product name, product code, brand, expiry...'
                  : 'Select source warehouse first'
              }
              className="w-full rounded-lg border px-3 py-3 text-sm disabled:bg-gray-100"
            />

            {scannedRows.length > 1 && <p className="mt-1 text-sm text-amber-700">This barcode matches multiple inventory batches. Select the required batch below.</p>}

            {showSuggestions && (suggestions.length > 0 || missingMatches.length > 0 || (!isInternalPacking && search.trim().length >= 2)) && (
              <div className="absolute z-30 mt-1 max-h-96 w-full overflow-auto rounded-xl border bg-white shadow-lg">
                {suggestions.map((group) => {
                  const first = group.rows[0];
                  const expiry = group.rows.map(row => row.exp_date_only).filter(Boolean).sort()[0];
                  const packs = group.rows.map(row => `${formatPackUnit(row)} × ${formatDecimal(row.available_units)}`).join(' · ');
                  const vendorBarcodes = [...new Set(group.rows.map(row => row.vendor_barcode).filter(Boolean))].join(', ');
                  const panel = expandedSuggestion?.key === group.key ? expandedSuggestion.mode : null;
                  const isWeight = packGrams(first) != null;
                  return (
                    <div key={group.key} className="border-b">
                      <div className="flex flex-wrap items-center gap-2 px-3 py-2 hover:bg-blue-50">
                        <button type="button" onMouseDown={(e) => e.preventDefault()}
                          onClick={() => toggleSuggestionPanel(group, 'available')}
                          title={`Code: ${first.product_code || '-'} | Brand: ${first.brand_name_english || '-'} | Category: ${first.category_name_english || '-'}`}
                          className="min-w-0 flex-1 truncate text-left text-sm">
                          <span className="font-bold text-gray-900">{first.display_product_name}</span>
                          <span className="text-gray-500"> · </span>
                          <span className="font-semibold text-green-700">{packs}</span>
                          <span className="text-gray-500"> · VB {vendorBarcodes || '-'}{expiry ? ` · Exp ${expiry}` : ''} · {first.product_code || '-'} · {first.brand_name_english || '-'}</span>
                        </button>
                        <div className="flex shrink-0 gap-1">
                          {[['available', 'Available'], ['custom', 'Custom']].map(([mode, text]) => (
                            <button key={mode} type="button" onMouseDown={(e) => e.preventDefault()}
                              onClick={() => toggleSuggestionPanel(group, mode)}
                              aria-label={`${text} ${first.display_product_name}`}
                              className={`rounded border px-2 py-1 text-xs font-semibold ${panel === mode ? 'border-indigo-600 bg-indigo-600 text-white' : 'border-indigo-300 bg-white text-indigo-700 hover:bg-indigo-50'}`}>
                              {text}
                            </button>
                          ))}
                        </div>
                      </div>
                      {panel === 'available' && (
                        <div className="bg-gray-50 px-3 pb-2">
                          {group.rows.map((row) => (
                            <button key={`${row.inventory_product_id}-${row.product_barcode_id}`} type="button"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => addInventoryItem(row, { keepOpen: true })}
                              className="flex w-full justify-between gap-3 border-t px-2 py-1 text-left text-xs hover:bg-blue-50">
                              <span>{formatPackUnit(row)} · Inv {row.inventory_product_id} · VB {row.vendor_barcode || '-'} · MK {row.display_barcode || '-'} · Exp {row.exp_date_only || '-'}</span>
                              <span className="font-semibold text-green-700">Avail {formatDecimal(row.available_units)} · Add</span>
                            </button>
                          ))}
                          <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => addAllAvailable(group)}
                            className="mt-1 rounded border border-green-600 bg-white px-2 py-1 text-xs font-semibold text-green-700 hover:bg-green-50">
                            Add all available
                          </button>
                        </div>
                      )}
                      {panel === 'custom' && (() => {
                        const sources = getRepackSources(group);
                        const plan = !isInternalPacking ? describeRepack(group) : null;
                        return (
                          <div className="space-y-2 bg-gray-50 px-3 py-2 text-xs">
                            {!isInternalPacking && (
                              <label className="flex flex-wrap items-center gap-2">
                                <span className="font-semibold text-gray-700">Source pack</span>
                                <select aria-label={`Source pack for ${first.display_product_name}`} value={repackEntry.sourceId}
                                  onChange={(e) => setRepackEntry(previous => ({ ...previous, sourceId: e.target.value }))}
                                  className="min-w-0 flex-1 rounded border px-2 py-1">
                                  {!sources.length && <option value="">No stock to repack</option>}
                                  {sources.map(row => (
                                    <option key={row.inventory_product_id} value={row.inventory_product_id}>
                                      {formatPackUnit(row)} · Inv {row.inventory_product_id} · Exp {row.exp_date_only || '-'} · Avail {formatDecimal(row.available_units)}
                                    </option>
                                  ))}
                                </select>
                              </label>
                            )}
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="font-semibold text-gray-700">Pack size</span>
                              <input type="number" min="0" step="any" aria-label={`Custom quantity for ${first.display_product_name}`}
                                value={customEntry.amount} placeholder={isWeight ? '750' : 'Units'}
                                onChange={(e) => setCustomEntry(previous => ({ ...previous, amount: e.target.value }))}
                                className="w-20 rounded border px-2 py-1" />
                              {isWeight ? (
                                <select aria-label="Custom quantity unit" value={customEntry.unit}
                                  onChange={(e) => setCustomEntry(previous => ({ ...previous, unit: e.target.value }))}
                                  className="rounded border px-2 py-1">
                                  <option value="gms">GMS</option>
                                  <option value="kg">KG</option>
                                </select>
                              ) : <span className="text-gray-500">units</span>}
                              {!isInternalPacking && <>
                                <span className="font-semibold text-gray-700">× Packs</span>
                                <input type="number" min="1" step="1" aria-label={`Number of packs for ${first.display_product_name}`}
                                  value={repackEntry.packs}
                                  onChange={(e) => setRepackEntry(previous => ({ ...previous, packs: e.target.value }))}
                                  className="w-16 rounded border px-2 py-1" />
                              </>}
                              <button type="button" disabled={repacking} onMouseDown={(e) => e.preventDefault()} onClick={() => addCustomQuantity(group)}
                                className="rounded bg-indigo-700 px-3 py-1 font-semibold text-white hover:bg-indigo-800 disabled:opacity-60">
                                {isInternalPacking ? 'Add' : repacking ? 'Packing…' : 'Pack & add'}
                              </button>
                            </div>
                            <p className={plan && !plan.enough ? 'text-red-700' : 'text-gray-500'}>
                              {isInternalPacking ? 'Creates a custom pack from the biggest source pack.'
                                : plan ? `Uses ${plan.sourceUnits} × ${formatPackUnit(plan.source)} of ${formatDecimal(plan.source.available_units)} available. The packs are made now and added to this dispatch.`
                                  : 'Choose a source pack and pack size. The packs are made now (Internal Packing) and added to this dispatch.'}
                            </p>
                          </div>
                        );
                      })()}
                    </div>
                  );
                })}
                {missingMatches.length > 0 && (
                  <div className="border-t-2 border-amber-200">
                    <div className="bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-900">Not in stock — goods that existed before the application</div>
                    {missingMatches.map(({ pack, zeroRow }) => {
                      const name = pack.product_name_eng || pack.product_name_tel || pack.product_code || 'Product';
                      return (
                        <div key={pack.id} className="border-b">
                          <div className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm">
                            <span className="min-w-0 flex-1 truncate">
                              <span className="font-bold text-gray-900">{name}</span>
                              <span className="text-gray-500"> · {formatPackUnit(pack)} · </span>
                              <span className="font-semibold text-amber-700">{zeroRow ? `Inv ${zeroRow.inventory_product_id} · 0 in stock` : 'No inventory'}</span>
                              <span className="text-gray-500"> · VB {pack.barcode || zeroRow?.vendor_barcode || '-'} · {pack.product_code || '-'} · {pack.brand_name_english || '-'}</span>
                            </span>
                            {zeroRow ? (
                              <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => addWithStockAdjust(zeroRow)}
                                aria-label={`Add with stock adjustment ${name}`}
                                className="shrink-0 rounded border border-amber-500 bg-white px-2 py-1 text-xs font-semibold text-amber-800 hover:bg-amber-50">
                                Add (adjust stock)
                              </button>
                            ) : (
                              <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => openStockForm({ pack })}
                                aria-label={`Create and add ${name}`}
                                className="shrink-0 rounded border border-amber-500 bg-white px-2 py-1 text-xs font-semibold text-amber-800 hover:bg-amber-50">
                                Create & add
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {!isInternalPacking && search.trim().length >= 2 && (
                  <div className="flex flex-wrap items-center justify-between gap-2 border-t bg-gray-50 px-3 py-2 text-xs">
                    <span className="text-gray-600">Product or pack not in the catalog?</span>
                    <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => openStockForm({ name: search.trim() })}
                      className="rounded border border-amber-500 bg-white px-2 py-1 font-semibold text-amber-800 hover:bg-amber-50">
                      New product…
                    </button>
                  </div>
                )}
              </div>
            )}
            {stockForm && !isInternalPacking && (
              <OpeningStockDispatchForm key={stockForm.pack ? `pack-${stockForm.pack.id}` : `new-${stockForm.name}`}
                pack={stockForm.pack || null} initialName={stockForm.name || ''} warehouseId={sourceWarehouseId} units={units}
                onCreated={handleOpeningStockCreated} onCancel={() => setStockForm(null)} />
            )}
            {pendingAdd && <p role="status" className="mt-1 text-xs text-gray-600">Adding the new stock to this dispatch…</p>}
          </div>
        </div>

        <div className="overflow-x-auto rounded-xl border bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100 text-gray-700">
              <tr>
                <th className="p-2 text-left">Product</th>
                <th className="p-2 text-left">Barcode</th>
                <th className="p-2 text-left">Brand</th>
                <th className="p-2 text-left">Category</th>
                <th className="p-2 text-center">Expiry</th>
                <th className="p-2 text-center">Available</th>
                <th className="p-2 text-center">Dispatch By</th>
                <th className="p-2 text-center">Dispatch Units</th>
                <th className="p-2 text-center">Unit</th>
                {!isInternalPacking && <th className="p-2 text-left">Margin / Price</th>}
                <th className="p-2 text-left">Notes</th>
                <th className="p-2 text-center">Remove</th>
              </tr>
            </thead>

            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={isInternalPacking ? 11 : 12} className="p-8 text-center">
                    <div className="font-semibold text-gray-700">No products added</div>
                    <div className="mt-1 text-xs text-gray-500">
                      Search inventory above and add products to create a dispatch.
                    </div>
                  </td>
                </tr>
              ) : (
                items.map((item, index) => {
                  const looseQtyPerUnit = getLooseQtyPerUnit(item);
                  const dispatchUnits = getDispatchUnitsFromEntry(item);
                  const pricing = getCurrentDispatchPricing(item);

                  return (
                    <tr
                      key={`${item.inventory_product_id}-${index}`}
                      className={`border-t ${!isInternalPacking && pricing.exceedsMrp ? 'bg-red-50' : ''}`}
                    >
                    <td className="p-2">
                      <div className="font-semibold">
                        {[item.product_code, item.product_name].filter(Boolean).join(' - ')}
                      </div>
                      <div className="text-xs text-gray-500">
                        Inventory ID: {item.inventory_product_id}
                      </div>
                    </td>

                    <td className="p-2">{item.mk_barcode || item.barcode || '-'}</td>
                    <td className="p-2">{item.brand_name_english || '-'}</td>
                    <td className="p-2">{item.category_name_english || '-'}</td>
                    <td className="p-2 text-center">
                      {item.needs_expiry ? (
                        <>
                          <input
                            type="date"
                            aria-label={`Expiry date for ${item.product_name || item.product_barcode_id}`}
                            value={item.exp_date || ''}
                            onChange={e => updateItem(index, 'exp_date', e.target.value)}
                            className="border rounded px-2 py-1"
                          />
                          <div className="text-xs text-amber-700 mt-1">Enter the actual batch expiry date.</div>
                        </>
                      ) : item.exp_date}
                    </td>

                    <td className="p-2 text-center font-bold text-green-700">
                      {formatDecimal(item.available_units)}
                      {item.auto_adjust_stock && Number(getSourceUnitsForPayload(item)) > Number(item.available_units || 0) && (
                        <div className="mt-1 text-xs text-amber-800">Add {formatDecimal(Number(getSourceUnitsForPayload(item)) - Number(item.available_units || 0))} units on Create; stock becomes {formatDecimal(getSourceUnitsForPayload(item))}.</div>
                      )}
                    </td>

                    <td className="p-2 text-center">
                      {looseQtyPerUnit ? (
                        <select
                          value={item.dispatch_entry_mode || 'units'}
                          onChange={(e) =>
                            updateItem(index, 'dispatch_entry_mode', e.target.value)
                          }
                          className="rounded border px-2 py-1 text-sm"
                        >
                          <option value="units">Units</option>
                          <option value="qty">Qty</option>
                        </select>
                      ) : (
                        <span className="text-gray-500">Units</span>
                      )}
                    </td>

                    <td className="p-2 text-center">
                      <input
                        type="number"
                        min="0.001"
                        step="any"
                        max={
                          item.auto_adjust_stock ? undefined : item.dispatch_entry_mode === 'qty' && looseQtyPerUnit
                            ? Number(item.available_units || 0) * looseQtyPerUnit
                            : item.available_units
                        }
                        value={
                          isInternalPacking
                            ? formatDecimal(getConfiguredSourceUnits(item))
                            : item.dispatch_entry_mode === 'qty'
                              ? item.dispatch_qty
                            : item.no_of_units
                        }
                        onChange={(e) =>
                          updateItem(
                            index,
                            item.dispatch_entry_mode === 'qty'
                              ? 'dispatch_qty'
                              : 'no_of_units',
                            e.target.value
                          )
                        }
                        readOnly={isInternalPacking}
                        className="w-24 rounded border px-2 py-1 text-center"
                      />
                      {item.dispatch_entry_mode === 'qty' && looseQtyPerUnit ? (
                        <div className="mt-1 text-[11px] text-gray-500">
                          {formatDecimal(dispatchUnits)} units
                        </div>
                      ) : null}
                    </td>

                    <td className="p-2 text-center">
                      {item.dispatch_entry_mode === 'qty' && looseQtyPerUnit
                        ? `QTY (${formatDecimal(looseQtyPerUnit, 0)} / ${formatPackUnit(item)})`
                        : formatPackUnit(item)}
                    </td>

                    {!isInternalPacking && (
                      <td className="min-w-[220px] p-2">
                        <label className="block text-xs font-semibold">
                          {pricing.pricingBasis === 'stakeholder_discount' ? 'Stakeholder discount (%)' : 'Ledger margin (%)'}
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            required
                            value={pricing.pricingBasis === 'stakeholder_discount' ? pricing.configuredDiscount : pricing.margin}
                            disabled={pricing.cost <= 0}
                            readOnly
                            aria-invalid={pricing.exceedsMrp}
                            aria-describedby={pricing.exceedsMrp ? 'dispatch-price-warning-' + index : undefined}
                            className={`mt-1 w-24 rounded border px-2 py-1 ${pricing.exceedsMrp ? 'border-red-600 bg-red-50 text-red-800' : ''}`}
                          />
                        </label>
                        <div className={`mt-1 text-xs ${pricing.exceedsMrp ? 'rounded bg-red-100 p-1 font-bold text-red-800' : ''}`}>D.Price: ₹{pricing.price.toFixed(2)} · MRP: {pricing.mrp > 0 ? '₹' + pricing.mrp.toFixed(2) : 'Not available'}</div>
                        {pricing.pricingBasis === 'stakeholder_discount' && <div className="mt-1 text-xs text-emerald-800">Discount per pack: ₹{(pricing.mrp - pricing.price).toFixed(2)} · MRP unchanged</div>}
                        {pricing.cappedAtMrp && <div className="mt-1 text-xs text-amber-800">Automatically reduced from ₹{pricing.uncappedPrice.toFixed(2)} to MRP.</div>}
                        <div className="mt-1 text-xs text-gray-600">{pricing.ledgerConfigId != null ? `Margin rule #${pricing.ledgerConfigId}` : 'No matching margin rule.'}</div>
                        {pricing.pricingError && <p role="alert" className="mt-1 text-xs text-red-700">{pricing.pricingError}</p>}
                        {pricing.exceedsMrp && (
                          <p id={'dispatch-price-warning-' + index} role="alert" className="mt-1 text-xs font-semibold text-red-700">
                            Price exceeds MRP by ₹{(pricing.price - pricing.mrp).toFixed(2)}. Customise the margin to bring the price below MRP.
                            {pricing.cost >= pricing.mrp && ' Source cost is already at or above MRP; review the source cost and MRP.'}
                          </p>
                        )}
                        {pricing.cost <= 0 && <p className="text-xs text-amber-700">Source cost unavailable; using the inventory price.</p>}
                      </td>
                    )}
                    <td className="p-2">
                      <input
                        value={item.notes || ''}
                        onChange={(e) => updateItem(index, 'notes', e.target.value)}
                        className="w-44 rounded border px-2 py-1"
                        placeholder="Optional"
                      />
                    </td>

                    <td className="p-2 text-center">
                      <button
                        type="button"
                        onClick={() => removeItem(index)}
                        className="rounded-lg bg-red-100 px-3 py-1 text-red-700 hover:bg-red-200"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {isInternalPacking && items.length > 0 && (
          <div className="rounded-xl border bg-white p-4">
            <div className="mb-3">
              <h3 className="text-base font-bold text-gray-900">
                Packing Configuration
              </h3>
              <p className="text-sm text-gray-500">
                Choose pack sizes from product barcodes. Prices use the current applicable market rate and internal packing margin. Dispatch recalculates with its applicable margin. Purchase cost remains unchanged.
              </p>
            </div>

            <div className="space-y-5">
              {items.map((item, itemIndex) => {
                const matches = getPackingBarcodeMatches(item);
                const selectedConfigIds = new Set(
                  (item.packing_configs || []).map((config) =>
                    String(config.product_barcode_id)
                  )
                );

                return (
                  <div key={`packing-${item.inventory_product_id}-${itemIndex}`}>
                    <div className="mb-2 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                      <div className="col-span-full min-w-0">
                        <div className="break-words font-bold text-gray-900">
                          {[item.product_code, item.product_name]
                            .filter(Boolean)
                            .join(' - ')}
                          </div>
                          <div className="text-xs text-gray-500">
                          {getPackingUsageText(item)}
                          </div>
                        </div>

                      <label className="block text-xs font-semibold text-gray-600">
                        Cost per Source Pack
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.source_unit_price || getSourceUnitPrice(item) || ''}
                          onChange={(e) =>
                            updateItem(itemIndex, 'source_unit_price', e.target.value)
                          }
                          className="mt-1 w-full rounded-lg border px-3 py-2 text-sm"
                          placeholder="Cost per source pack"
                        />
                      </label>

                      <label className="block text-xs font-semibold text-gray-600">
                        Repacking Cost
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.repacking_cost_total || ''}
                          onChange={(e) =>
                            updateItem(itemIndex, 'repacking_cost_total', e.target.value)
                          }
                          className="mt-1 w-full rounded-lg border px-3 py-2 text-sm"
                          placeholder="Optional total"
                        />
                      </label>

                      <label className="block text-xs font-semibold text-gray-600">
                        Source Pack
                        <input
                          type="number"
                          min="0"
                          step="0.001"
                          value={
                            isWeightUnit(item)
                              ? item.barcode_quantity || item.quantity || ''
                              : item.source_pack_quantity || ''
                          }
                          onChange={(e) =>
                            updateItem(itemIndex, 'source_pack_quantity', e.target.value)
                          }
                          readOnly={isWeightUnit(item)}
                          className="mt-1 w-full rounded-lg border px-3 py-2 text-sm"
                          placeholder="50"
                        />
                      </label>

                      <label className="block text-xs font-semibold text-gray-600">
                        Source Unit
                        <select
                          value={
                            isWeightUnit(item)
                              ? item.unit_id || ''
                              : item.source_pack_unit_id || ''
                          }
                          onChange={(e) => {
                            const unit = getUnitById(e.target.value);
                            updateItem(itemIndex, 'source_pack_unit_id', e.target.value);
                            updateItem(
                              itemIndex,
                              'source_pack_unit_short_code',
                              unit.unit_short_code || unit.unit_name || ''
                            );
                          }}
                          disabled={isWeightUnit(item)}
                          className="mt-1 w-full rounded-lg border px-3 py-2 text-sm"
                        >
                          <option value="">Unit</option>
                          {(units || []).map((unit) => (
                            <option key={unit.id} value={unit.id}>
                              {unit.unit_short_code || unit.unit_name}
                            </option>
                          ))}
                        </select>
                      </label>

                      <select
                        value=""
                        onChange={(e) => {
                          const selected = matches.find(
                            (match) =>
                              String(match.id || match.product_barcode_id) ===
                              e.target.value
                          );
                          if (selected) addPackingConfig(itemIndex, selected);
                        }}
                        className="col-span-full min-w-0 w-full rounded-lg border px-3 py-2 text-sm"
                      >
                        <option value="">
                          {matches.length
                            ? 'Add packing size'
                            : 'No product barcode pack sizes found'}
                        </option>
                        {matches.map((match) => {
                          const id = String(match.id || match.product_barcode_id);
                          return (
                            <option
                              key={id}
                              value={id}
                              disabled={selectedConfigIds.has(id)}
                            >
                              {[
                                match.product_name_eng ||
                                  match.product_name ||
                                  item.product_name,
                                formatPackUnit(match),
                                  match.mk_barcode ||
                                    match.mkBarcode ||
                                    match.MKBarcode ||
                                    match.bar_code ||
                                    match.barcode,
                              ]
                                .filter(Boolean)
                                .join(' | ')}
                            </option>
                          );
                        })}
                      </select>
                    </div>

                    <div className="mb-3 rounded-lg border bg-indigo-50 p-3">
                      <div className="flex flex-wrap gap-2">
                        {['regular', 'budget'].map(kind => <button key={kind} type="button"
                          onClick={() => setPresetPanels(previous => ({ ...previous, [`${item.inventory_product_id}-${kind}`]: !previous[`${item.inventory_product_id}-${kind}`] }))}
                          className="rounded-lg border border-indigo-300 bg-white px-3 py-2 text-sm font-semibold">
                          {kind === 'regular' ? 'Regular' : 'Budget friendly'}
                        </button>)}
                        <button type="button" onClick={() => setCustomFocusId(String(item.inventory_product_id))}
                          className="rounded-lg border border-indigo-300 bg-white px-3 py-2 text-sm font-semibold">
                          Custom
                        </button>
                      </div>
                      {presetPanels[`${item.inventory_product_id}-regular`] && <div className="mt-3 flex flex-wrap gap-4">
                        {[25, 50, 100, 250, 500, 1000].map(grams => {
                          const key = `regular-${grams}`;
                          return <label key={key} className="flex items-center gap-2 text-sm"><input type="checkbox"
                            checked={item.packing_configs.some(config => config.preset_key === key)}
                            onChange={event => togglePresetPacking(itemIndex, key, grams, event.target.checked)} />{grams === 1000 ? '1 KG' : `${grams} GMS`}</label>;
                        })}
                      </div>}
                      {presetPanels[`${item.inventory_product_id}-budget`] && <div className="mt-3">
                        <p className="mb-2 text-xs text-gray-600">Selling price uses stock purchase cost, packing cost and the configured margin. MRP is scaled from the base stock MRP by pack size and rounded up. Rupee options set the selling price limit. Tick to add one pack; adjust pack count below.</p>
                        {packingLedgerError && <p role="alert" className="mb-2 text-sm text-red-600">{packingLedgerError}</p>}
                        <div className="flex flex-wrap gap-4">{[10, 20, 30, 40, 50].map(budget => {
                          const key = `budget-${budget}`;
                          const existing = item.packing_configs.find(config => config.preset_key === key);
                          const grams = getBudgetGrams(item, budget);
                          return <label key={key} className="flex items-center gap-2 text-sm"><input type="checkbox"
                            disabled={!existing && !grams} checked={Boolean(existing)}
                            onChange={event => togglePresetPacking(itemIndex, key, grams, event.target.checked)} />₹{budget} — {existing ? existing.barcode_quantity : grams || 'Not configured'}{(existing || grams) ? ' GMS' : ''}</label>;
                        })}</div>
                        <p className="mt-2 text-xs text-gray-600">Untick and tick again to recalculate grams after changing source cost or market configuration. Prices update automatically; packing costs are allocated across the selected packs.</p>
                      </div>}
                    </div>

                    <div className="mb-3 grid gap-2 rounded-lg border bg-gray-50 p-3 md:grid-cols-[140px_140px_140px] md:items-end">
                      <label className="block text-xs font-semibold text-gray-600">
                        Pack Qty
                        <input
                          type="number"
                          min="0"
                          step="1"
                          data-custom-pack-qty={item.inventory_product_id}
                          value={dynamicPackForms[itemIndex]?.quantity || ''}
                          onChange={(e) =>
                            updateDynamicPackForm(itemIndex, 'quantity', e.target.value)
                          }
                          className="mt-1 w-full rounded border px-2 py-1 text-sm"
                          placeholder="500"
                        />
                      </label>

                      <label className="block text-xs font-semibold text-gray-600">
                        Unit
                        <select
                          value={
                            dynamicPackForms[itemIndex]?.unit_id || getDefaultPackUnitId()
                          }
                          onChange={(e) =>
                            updateDynamicPackForm(itemIndex, 'unit_id', e.target.value)
                          }
                          className="mt-1 w-full rounded border px-2 py-1 text-sm"
                        >
                          <option value="">Unit</option>
                          {(units || []).map((unit) => (
                            <option key={unit.id} value={unit.id}>
                              {unit.unit_short_code || unit.unit_name}
                            </option>
                          ))}
                        </select>
                      </label>

                      <button
                        type="button"
                        onClick={() => addDynamicPackingConfig(itemIndex)}
                        className="rounded-lg bg-indigo-700 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-800"
                      >
                        Create pack
                      </button>
                    </div>

                    <div className="overflow-x-auto rounded-lg border">
                      <table className="min-w-[760px] w-full text-xs">
                        <thead className="bg-gray-100 text-gray-700">
                          <tr>
                            <th className="p-2 text-left">Product</th>
                            <th className="p-2 text-left">Pack Size</th>
                            <th className="p-2 text-right">Price / Pack</th>
                            <th className="p-2 text-right">MRP / Pack</th>
                            <th className="p-2 text-left">Pricing Basis</th>
                            <th className="p-2 text-center">Pack Count</th>
                            <th className="p-2 text-center">Remove</th>
                          </tr>
                        </thead>

                        <tbody>
                          {(item.packing_configs || []).length === 0 ? (
                            <tr>
                              <td
                                colSpan="7"
                                className="p-4 text-center text-gray-500"
                              >
                                Add at least one packing size for this item.
                              </td>
                            </tr>
                          ) : (
                            item.packing_configs.map((config, configIndex) => (
                              <tr
                                key={`${config.product_barcode_id || 'dynamic'}-${configIndex}`}
                                className="border-t"
                              >
                                <td className="p-2 font-semibold">
                                  {config.mk_barcode ||
                                    config.mkBarcode ||
                                    config.MKBarcode ||
                                    config.bar_code ||
                                    config.barcode ||
                                    (config.dynamic_pack
                                      ? makeMkBarcode({
                                          product_id: config.product_id || item.product_id,
                                          brand_id: config.brand_id || item.brand_id,
                                          category_id: config.category_id || item.category_id,
                                          unit_id: config.unit_id,
                                          quantity: config.barcode_quantity || config.quantity,
                                        })
                                      : '') ||
                                    (config.dynamic_pack ? 'Auto create barcode' : '') ||
                                    '-'}
                                </td>
                                  <td className="p-2">
                                    {getPackingProductName(
                                      config.product_name || item.product_name,
                                      item.brand_name_english || item.brand_name
                                    )} -{' '}
                                    {formatPackUnit(config)}
                                  </td>
                                <td className="p-2 text-right">{getConfigPrices(item, config) ? `₹${getConfigPrices(item, config).price.toFixed(2)}` : 'Not configured'}</td>
                                <td className="p-2 text-right">{getConfigPrices(item, config) ? `₹${getConfigPrices(item, config).mrp}` : 'Not configured'}</td>
                                <td className="p-2">{getConfigPrices(item, config)?.pricing_basis === 'market' ? `Market ₹${getConfigPrices(item, config).market_rate_per_kg}/KG` : 'Purchase cost'}</td>
                                <td className="p-2 text-center">
                                  <input
                                    type="number"
                                    min="0"
                                    step="1"
                                    value={config.pack_count}
                                    onChange={(e) =>
                                      updatePackingConfig(
                                        itemIndex,
                                        configIndex,
                                        'pack_count',
                                        e.target.value
                                      )
                                    }
                                    className="w-20 rounded border px-2 py-1 text-center"
                                  />
                                </td>
                                <td className="p-2 text-center">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      removePackingConfig(itemIndex, configIndex)
                                    }
                                    className="rounded-lg bg-red-100 px-3 py-1 text-red-700 hover:bg-red-200"
                                  >
                                    Remove
                                  </button>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="flex justify-end border-t pt-4">
          <button
            type="submit"
            disabled={loading}
            className="rounded-lg bg-blue-700 px-5 py-2 font-semibold text-white hover:bg-blue-800 disabled:opacity-60"
          >
            {loading ? 'Saving...' : 'Create Dispatch'}
          </button>
        </div>
      </form>
    </section>
  );
};

export default CreateDispatchOrderSection;
